import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { s4aDone, s4bDone } from './section4Steps';
import { docText as _s4abText, currentLang } from './docText';
import { isTaskOverdue, OverdueLabel, OVERDUE_RED } from './taskOverdue';
import { useNavigate, useLocation, useMatch } from 'react-router-dom';
import { useDarkMode, DarkModeToggle } from './DarkModeContext';
import EmployeeProfile from './EmployeeProfile';
import TradeActions from './TradeActions';
import EmployeeView from './EmployeeView';
import ComplianceModule from './ComplianceModule';
import PolicyRepository from './PolicyRepository';
// POLICYHR2: the policy write allowlist, in its own module so no test mock can hide it.
import { POLICY_WRITE_ROLES } from './policyRoles';
import RetentionReview from './RetentionReview';
import ApplicationRequestDashboard from './ApplicationRequestDashboard';
import AddEmployee from './AddEmployee';
import Notifications from './Notifications';
import LeaverForm from './LeaverForm';
import HRReport from './HRReport';
import ComplianceReport from './ComplianceReport';
import PerformanceReport from './PerformanceReport';
import GrievanceList from './GrievanceList';
import WorkflowList from './WorkflowList';
import ValidationView from './ValidationView';
import { OPENABLE_UPLOAD_TASKS } from './emailLinks';
// REDESIGN Stage 1: the approved design tokens (ENKO_DESIGN_REVIEW.html). Presentation only -
// mkS below now resolves its colours, type and spacing from these instead of hardcoded hexes.
import {
  IconDashboard,
  IconUser,
  IconUserPlus,
  IconUserMinus,
  IconShield,
  IconFiles,
  IconChart,
  IconTools,
  IconCheck,
  IconTrend,
  // HRSHELL: the Leave calendar nav entry's icon. IconUsers is an EXISTING icon reused as-is;
  // no calendar icon exists in icons.js and this build designs nothing new.
  IconUsers,
} from './icons';
import { tokens, RADIUS } from './theme';
// Review items 5/6: gold is now only ever an accent (icon/hover outline), never a fill.
import { GOLD_500 } from './colors';
import { AccessWait, InternBadge, LoadError, useConfirm, useIsNarrow, ScrollableTable, gridCols, Sidebar } from './ui';
// OPSTASKSALL: the COO's Operational Tasks card, shared with every role that handles tasks.
import OperationalTasksCard from './OperationalTasksCard';
import { mkS } from './dashboardStyles';
import { isInternshipEnded, internshipDaysRemaining, endDateYMD } from './internship';
import { ROLE_DEFAULTS } from './roleDefaults';
import { consumePostLoginNext, peekPostLoginNext, setOpenFpAssessment } from './postLoginNext';
// HRSHELL: the HR shell's section/URL declaration, so this component's URL maps and the HR
// sidebar both derive from ONE list instead of restating it.
import { HR_SHELL_ROUTES } from './shellRoutes';
import AbsenceRegister from './AbsenceRegister';
import NotificationBell from './NotificationBell';
import LeaveCalendar from './LeaveCalendar';
// DATEDAY: date-only values shown as their calendar day in every timezone.
import { toDisplayDate } from './dateOnly';
import { isWorkflowStepTask, workflowStepOpenPath, completeWorkflowStepTask } from './workflowStepTask';
import { fpStepRefusal, fpAssessmentOfTask } from './FPAssessmentShared';

// POLICYHR2: the allowlist MOVED to PolicyRepository.js and is imported above. It was declared
// here, which meant it could only gate THIS mount - the App.js mount hardcoded its own rule and
// disagreed. One exported list now gates every mount. See PolicyRepository.js for why.
//
// POLICYHR2 also ADDED hr: Marie-Lynne's decision is that HR manages the Policy Repository, so hr
// may upload, replace and archive exactly as the coo may. The server allowlist names the same four
// roles and a verify script parses both files to prove they agree.

// POLICYHR: the roles the SERVER lets read the absence register, mirrored from
// backend/app/services/absence_register.py's REGISTER_ROLES, which is the single allowlist
// its router gate is built from. Same positive-list rule as above.
const ABSENCE_REGISTER_ROLES = ['hr', 'coo', 'admin'];

const API = '/api';

// FINSIDEBAR: same helper already used verbatim by WorkflowList.js (TESTBTNHIDE) and the QA-only
// "Fill demo data" controls in AddEmployee.js / NewStarterForm.js / LeaverForm.js / SponsorForm.js
// / ComplianceModule.js / ApplicationRequestForm.js - reads the is_test_account flag the backend
// returns at login (auth.py: account.email === settings.TEST_COO_EMAIL) and App.js stores in
// enko_auth. Copied rather than imported to match the existing convention in all 7 of those files.
const isTestAccount = () => { try { return JSON.parse(localStorage.getItem('enko_auth'))?.is_test_account === true; } catch { return false; } };

const TASK_COLOURS = { overdue: '#A63A3A', pending: '#c9a84c', doneOnTime: '#2D4A3E', doneLate: '#5C4A2A' };

const STATUS_COLOURS = {
  'Overdue':      '#A63A3A',
  'Pending':      '#c9a84c',
  'Done on time': '#2D4A3E',
  'Done late':    '#5C4A2A',
};

// ── Icons ─────────────────────────────────────────────────────────────────────

// ── Modals (existing logic, unchanged) ────────────────────────────────────────
export function PendingTasksModal({ onClose, onSelectEmployee }) {
  const { isDarkMode } = useDarkMode();
  const m = mkM(isDarkMode);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  // PENDTASKFAILSAFE: distinct from `loading`. A failed load left `tasks` at its empty-array
  // default, rendering "No pending tasks" indistinguishable from a genuinely empty worklist.
  const [loadFailed, setLoadFailed] = useState(false);
  const [expandedEmployee, setExpandedEmployee] = useState(null);

  const load = () => {
    setLoading(true);
    setLoadFailed(false);
    axios.get(`${API}/dashboard/pending-tasks`)
      .then(res => { setTasks(res.data.pending_tasks); setLoading(false); })
      .catch(() => { setLoadFailed(true); setLoading(false); });
  };

  useEffect(() => { load(); }, []);

  const grouped = tasks.reduce((acc, task) => {
    const key = task.employee_id;
    if (!acc[key]) acc[key] = { employee_id: task.employee_id, employee_name: task.employee_name, tasks: [] };
    acc[key].tasks.push(task);
    return acc;
  }, {});
  const empList = Object.values(grouped);

  return (
    <div style={m.overlay}>
      <div style={m.modal}>
        <div style={m.header}>
          <h2 style={m.title}>Pending Tasks <span style={m.countBadge}>{tasks.length}</span></h2>
          <button style={m.closeBtn} onClick={onClose}>✕</button>
        </div>
        {loading ? <p style={m.empty}>Loading...</p>
          : loadFailed ? (
            // PENDTASKFAILSAFE: checked before the empty-state branch, so a failed load can
            // never render as "No pending tasks".
            <LoadError message="Could not load pending tasks." onRetry={load} />
          )
          : empList.length === 0 ? <p style={m.empty}>No pending tasks</p>
          : (
            <div style={m.employeeList}>
              {empList.map(emp => (
                <div key={emp.employee_id} style={m.employeeCard}>
                  <div style={m.employeeRow}
                    onClick={() => setExpandedEmployee(expandedEmployee === emp.employee_id ? null : emp.employee_id)}>
                    <div style={m.employeeLeft}>
                      <div style={m.avatar}>{emp.employee_name.charAt(0)}</div>
                      <div>
                        <p style={m.employeeName}>{emp.employee_name}</p>
                        <p style={m.taskCount}>
                          {emp.tasks.length} pending task{emp.tasks.length > 1 ? 's' : ''}
                          <span style={m.expandHint}>{expandedEmployee === emp.employee_id ? ' (collapse)' : ' (expand)'}</span>
                        </p>
                      </div>
                    </div>
                    <button style={m.viewProfileBtn}
                      onClick={e => { e.stopPropagation(); onClose(); onSelectEmployee(emp.employee_id); }}>
                      View profile
                    </button>
                  </div>
                  {expandedEmployee === emp.employee_id && (
                    <div style={m.taskList}>
                      {emp.tasks.map(task => (
                        <div key={task.task_id} style={m.taskItem}>
                          <div style={{ ...m.taskDot, ...(isTaskOverdue(task) ? { backgroundColor: OVERDUE_RED } : {}) }} />
                          <div>
                            <p style={m.taskTitle}>{task.task_title}</p>
                            <p style={m.taskDue}>Due: {task.due_date ? toDisplayDate(task.due_date).toLocaleDateString() : '-'}</p>
                          </div>
                          {/* TASKOVERDUE: the server lists overdue tasks first; they carry the label. */}
                          {isTaskOverdue(task) && <OverdueLabel />}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
      </div>
    </div>
  );
}

function VisaSponsoredModal({ onClose, onSelectEmployee, employees }) {
  const { isDarkMode } = useDarkMode();
  const m = mkM(isDarkMode);
  const visaEmployees = employees.filter(emp => emp.requires_visa);
  return (
    <div style={m.overlay}>
      <div style={m.modal}>
        <div style={m.header}>
          <h2 style={m.title}>Visa Sponsored <span style={m.countBadge}>{visaEmployees.length}</span></h2>
          <button style={m.closeBtn} onClick={onClose}>✕</button>
        </div>
        {visaEmployees.length === 0 ? <p style={m.empty}>No visa sponsored employees</p> : (
          <div style={m.employeeList}>
            {visaEmployees.map(emp => (
              <div key={emp.id} style={m.visaCard}
                onClick={() => { onClose(); onSelectEmployee(emp.id); }}>
                <div style={m.employeeLeft}>
                  <div style={m.avatar}>{emp.first_name.charAt(0)}</div>
                  <div>
                    <p style={m.employeeName}>{emp.first_name} {emp.last_name}</p>
                    <p style={m.visaEmail}>{emp.company_email}</p>
                  </div>
                </div>
                <span style={m.visaBadge}>Visa Required</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// LEAVERUI: modal to pick which employee to start a leaver process for. Replaces the old
// "Start a Leaver Process" candidate LIST on the Leavers page. Selecting someone here calls
// onSelectEmployee(emp.id), which sets the exact same leaverEmployee state the old "Start Leaver"
// row button set - so the underlying start action / leaver flow is completely unchanged; only how
// the employee is chosen moved from an always-visible list into this searchable dialog.
function StartLeaverModal({ onClose, onSelectEmployee, employees }) {
  const { isDarkMode } = useDarkMode();
  const m = mkM(isDarkMode);
  const [search, setSearch] = useState('');
  // Eligibility: active, not archived, and no leaver form row at all. LEAVERDRAFT tightened the
  // last condition: a saved DRAFT (row exists, section1 not submitted) also disqualifies -
  // resuming a draft happens from the Draft list or the profile, never as a fresh start here.
  const eligible = employees.filter(e =>
    e.is_active && !e.is_archived && !e._leaverInProgress
  );
  const term = search.trim().toLowerCase();
  const shown = term
    ? eligible.filter(e => `${e.first_name} ${e.last_name} ${e.company_email}`.toLowerCase().includes(term))
    : eligible;
  return (
    <div style={m.overlay}>
      <div style={m.modal}>
        <div style={m.header}>
          <h2 style={m.title}>Start a leaver process</h2>
          <button style={m.closeBtn} onClick={onClose}>✕</button>
        </div>
        <input
          autoFocus
          type="text"
          placeholder="Search an employee by name or email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            width: '100%', marginBottom: '16px', padding: '10px 12px',
            border: `1px solid ${isDarkMode ? 'rgba(201,168,76,0.2)' : '#ddd'}`,
            borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box',
            backgroundColor: isDarkMode ? '#0f1a30' : '#fff',
            color: isDarkMode ? '#e8e4d8' : '#1a1a2e',
          }}
        />
        {eligible.length === 0 ? (
          <p style={m.empty}>All active employees already have a leaver process started.</p>
        ) : shown.length === 0 ? (
          <p style={m.empty}>No matching employee.</p>
        ) : (
          <div style={m.employeeList}>
            {shown.map(emp => (
              <div key={emp.id} style={m.visaCard}
                onClick={() => { onClose(); onSelectEmployee(emp.id); }}>
                <div style={m.employeeLeft}>
                  <div style={m.avatar}>{emp.first_name.charAt(0)}</div>
                  <div>
                    <p style={m.employeeName}>{emp.first_name} {emp.last_name}</p>
                    <p style={m.visaEmail}>{emp.company_email}</p>
                  </div>
                </div>
                <span style={{ fontSize: '12px', color: '#666' }}>{emp.primary_entity || ''}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Task row with hover state ──────────────────────────────────────────────────
function TaskRow({ task, onClick }) {
  const { isDarkMode } = useDarkMode();
  const s = mkS(isDarkMode);
  const [hovered, setHovered] = React.useState(false);
  return (
    <tr
      style={{ ...s.tr, cursor: 'pointer', backgroundColor: hovered ? tokens(isDarkMode).hover : 'transparent', transition: 'background-color 0.12s' }}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <td style={s.td}>
        <span style={{ fontWeight: 500, fontSize: '13px', color: isDarkMode ? '#e8e4d8' : '#1a1a2e' }}>{task.title}</span>
      </td>
      <td style={s.td}>{task.employee_name}</td>
      <td style={s.td}><span style={{ fontSize: '12px', color: isDarkMode ? '#9ba8c0' : '#666' }}>{task.source_form}</span></td>
      <td style={s.td}><span style={{ fontSize: '12px', color: isDarkMode ? '#9ba8c0' : '#aaa' }}>{task.created_at ? toDisplayDate(task.created_at).toLocaleDateString() : '-'}</span></td>
    </tr>
  );
}

// ── Topbar button with gold hover ─────────────────────────────────────────────
function TopbarBtn({ children, onClick }) {
  const [h, setH] = React.useState(false);
  return (
    <button onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{ backgroundColor: h ? 'rgba(201,168,76,0.15)' : 'transparent', color: h ? GOLD_500 : '#fff', border: `1px solid ${h ? GOLD_500 : 'rgba(255,255,255,0.25)'}`, borderRadius: RADIUS.md, padding: '8px 14px', fontSize: '13.5px', cursor: 'pointer', transition: 'all 0.15s', fontWeight: 570 }}>
      {children}
    </button>
  );
}

// ── KPI card with hover state ─────────────────────────────────────────────────
function KpiCard({ label, value, accent, sub, onClick }) {
  const { isDarkMode } = useDarkMode();
  const [hovered, setHovered] = React.useState(false);
  const border = `1px solid ${tokens(isDarkMode).line}`;
  return (
    <div
      style={{
        backgroundColor: tokens(isDarkMode).surface, borderRadius: RADIUS.xl, padding: '18px 20px',
        boxShadow: hovered ? (isDarkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.1)') : (isDarkMode ? 'none' : '0 1px 3px rgba(0,0,0,0.05)'),
        borderTop: border, borderRight: border, borderBottom: border,
        borderLeft: `3px solid ${accent}`,
        cursor: 'pointer', transition: 'box-shadow 0.12s',
      }}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <p style={{ fontSize: '11px', color: isDarkMode ? '#9ba8c0' : '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 8px 0', fontWeight: 600 }}>{label}</p>
      <p style={{ fontSize: '28px', fontWeight: '700', margin: '0 0 4px 0', color: accent }}>{value}</p>
      <p style={{ fontSize: '11px', color: isDarkMode ? '#9ba8c0' : '#aaa', margin: 0 }}>{sub}</p>
    </div>
  );
}

// ── COO My Profile wrapper ────────────────────────────────────────────────────
// Fetches the logged-in user's employee record by email (fresh call, not relying
// on the pre-loaded/filtered employees list) and renders EmployeeView with a
// "← Back to dashboard" button above it.
function CooProfileView({ email, currentUser, onLogout, role, onBack }) {
  const { isDarkMode } = useDarkMode();
  const [empId, setEmpId] = React.useState(null);
  const [empEntity, setEmpEntity] = React.useState(null);
  const [loadingEmp, setLoadingEmp] = React.useState(true);
  const [notFound, setNotFound] = React.useState(false);
  const [hov, setHov] = React.useState(false);

  React.useEffect(() => {
    // "My Profile" always loads the logged-in user's own record via the self endpoint
    // (GET /employees/ and /employees/{id} are restricted to coo/admin/hr).
    axios.get(`${API}/employees/me`)
      .then(res => {
        const match = res.data;
        if (match) { setEmpId(match.id); setEmpEntity(match.primary_entity); }
        else setNotFound(true);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoadingEmp(false));
  }, [email]);

  const backBar = (
    <div style={{ padding: '10px 20px', background: isDarkMode ? '#0a1020' : tokens(isDarkMode).bg, borderBottom: `1px solid ${tokens(isDarkMode).line}` }}>
      <button
        onClick={onBack}
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        style={{
          fontSize: '12px', color: hov ? '#c9a84c' : '#1a2744',
          border: `1px solid ${hov ? GOLD_500 : tokens(isDarkMode).border}`,
          borderRadius: '6px', padding: '5px 12px', cursor: 'pointer',
          background: 'none', transition: 'color 0.15s, border-color 0.15s',
          fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
        }}
      >
        ← Back to dashboard
      </button>
    </div>
  );

  if (loadingEmp || notFound) {
    return (
      <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', background: isDarkMode ? '#0f1a2e' : '#FAFAF8', minHeight: '100vh' }}>
        {backBar}
        {!loadingEmp && notFound && (
          <div style={{ padding: '24px', color: '#aaa', fontSize: '14px' }}>No employee record found for {email}.</div>
        )}
      </div>
    );
  }

  return (
    <>
      {backBar}
      <EmployeeView
        employeeId={empId}
        currentUser={currentUser}
        onLogout={onLogout}
        userRole={role}
        employeeEntity={empEntity}
        onLogoClick={(resetToOverview) => resetToOverview()}
      />
    </>
  );
}

// EMPTABLESTABLE: the shared employee table (search + filters + rows), used by HR Overview and the
// Employees section. Defined at MODULE level, not inside Dashboard: previously it was a nested
// arrow function redefined on every Dashboard render, so every keystroke in the search box gave
// React a new component TYPE, remounting the whole subtree and blurring the input on each
// character. Hoisting it gives it a stable identity, so React updates the existing input in place
// and focus is preserved. Behaviour is byte-identical - all state, styles and the 300ms-debounced
// fetch still live on Dashboard and are passed in as props; this component holds no state of its
// own and changed no logic.
// EMPFILTERSORT: a generic, reusable sort helper so another column (e.g. Department) can be made
// sortable later by passing its field name - only Name is wired up now, as requested. Uses
// localeCompare for correct accented-name ordering, sorts by last_name then first_name, and puts
// empty values last regardless of direction so a blank name never floats to the top.
function sortBy(list, field, dir) {
  if (!dir) return list;
  const val = (e) =>
    field === 'name'
      ? `${(e.last_name || '').trim()} ${(e.first_name || '').trim()}`.trim()
      : (e[field] || '').toString().trim();
  const factor = dir === 'desc' ? -1 : 1;
  return [...list].sort((a, b) => {
    const av = val(a), bv = val(b);
    if (!av && !bv) return 0;
    if (!av) return 1;   // empties last, both directions
    if (!bv) return -1;
    return factor * av.localeCompare(bv, undefined, { sensitivity: 'base' });
  });
}

// HRFOLLOW point 5: the line manager cell of the COO and HR employee list.
// MGRPROFILE (2026-10-28, business decision): the list only DISPLAYS the line manager, the name or
// "None". Changing or removing it is done from the employee profile's Manager field, through the
// same PATCH /employees/{id}/manager; the list's Change link, picker and Remove manager are gone.
function ManagerCell({ emp }) {
  const { t } = useTranslation();
  return <span>{emp.manager_name || t('hrFollow.noManager')}</span>;
}

function EmployeeTable({ employees, empSearch, setEmpSearch, empStatus, setEmpStatus,
                         visaFilter, setVisaFilter, entityFilter, setEntityFilter,
                         teamFilter, setTeamFilter, nameSort, setNameSort,
                         isDarkMode, s, setSelectedEmployee,
                         canChangeManager = false,
                         deptFilter = '', setDeptFilter = () => {} }) {
  // HRPROFILE: the Team column beside Department.
  const { t: tableT } = useTranslation();
  // EMPFILTERSORT: dropdown options are deduped + sorted from the LOADED employees array (no
  // hardcoded lists), so they always reflect real values for the current status/search result set.
  const entityOptions = useMemo(
    () => [...new Set(employees.map(e => e.primary_entity).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [employees]
  );
  const teamOptions = useMemo(
    // HRPROFILE follow-up: the team filter lists the teams in use (function_team), not departments.
    () => [...new Set(employees.map(e => e.function_team).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [employees]
  );
  // EMPFILTERSORT: single derivation - filters (visa -> entity -> team) then sort LAST, so sorting
  // always applies to the filtered set. Memoized on every input that affects it. EMPTABLESTABLE:
  // this component is still a stable module-level function holding NO state of its own, so the
  // search input and dropdowns keep their focus across these re-renders.
  const displayedEmployees = useMemo(() => {
    let list = employees;
    if (visaFilter) list = list.filter(e => e.requires_visa);
    if (entityFilter) list = list.filter(e => e.primary_entity === entityFilter);
    if (teamFilter) list = list.filter(e => e.function_team === teamFilter);
    // Department filter, beside the Team filter, in the COO and HR lists (canChangeManager).
    if (canChangeManager && deptFilter) list = list.filter(e => e.department === deptFilter);
    return sortBy(list, 'name', nameSort);
  }, [employees, visaFilter, entityFilter, teamFilter, deptFilter, canChangeManager, nameSort]);
  // The departments in use, for the Department filter.
  const deptOptions = useMemo(
    () => [...new Set(employees.map(e => e.department).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [employees]
  );

  // Cycle the Name/Email header: none -> asc -> desc -> none.
  const toggleNameSort = () => setNameSort(prev => (prev === 'asc' ? 'desc' : prev === 'desc' ? null : 'asc'));
  const nameArrow = nameSort === 'asc' ? ' ▲' : nameSort === 'desc' ? ' ▼' : '';

  const selectStyle = {
    ...s.filterBtn, borderRight: '1px solid ' + (isDarkMode ? '#3a4a6a' : '#ddd'),
    cursor: 'pointer', appearance: 'auto',
    backgroundColor: isDarkMode ? '#243058' : '#fff', color: isDarkMode ? '#e8e4d8' : '#666',
  };

  return (
    <>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '14px', alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search by name..."
          value={empSearch}
          onChange={e => setEmpSearch(e.target.value)}
          style={s.searchInput}
        />
        <div style={s.filterGroup}>
          {['active', 'all', 'former'].map(st => (
            <button key={st} onClick={() => setEmpStatus(st)}
              style={{ ...s.filterBtn, backgroundColor: empStatus === st ? '#1a1a2e' : (isDarkMode ? '#243058' : '#fff'), color: empStatus === st ? '#fff' : (isDarkMode ? '#e8e4d8' : '#666') }}>
              {st.charAt(0).toUpperCase() + st.slice(1)}
            </button>
          ))}
          <button
            onClick={() => setVisaFilter(v => !v)}
            style={{ ...s.filterBtn, borderRight: 'none', backgroundColor: visaFilter ? '#1a1a2e' : (isDarkMode ? '#243058' : '#fff'), color: visaFilter ? '#fff' : (isDarkMode ? '#e8e4d8' : '#666') }}>
            Visa Sponsored
          </button>
        </div>
        {/* EMPFILTERSORT: country/entity + team(department) dropdowns, client-side over loaded data. */}
        <select aria-label="Filter by country / entity" value={entityFilter} onChange={e => setEntityFilter(e.target.value)} style={selectStyle}>
          <option value="">All entities</option>
          {entityOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
        <select aria-label="Filter by team" value={teamFilter} onChange={e => setTeamFilter(e.target.value)} style={selectStyle}>
          <option value="">All teams</option>
          {teamOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
        {canChangeManager && (
          <select aria-label={tableT('hrFollow.filterDepartment')} value={deptFilter}
            onChange={e => setDeptFilter(e.target.value)} style={selectStyle}>
            <option value="">{tableT('hrFollow.allDepartments')}</option>
            {deptOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        )}
      </div>
      {displayedEmployees.length === 0 ? <p style={s.empty}>No employees found</p> : (
        <ScrollableTable><table style={s.table}>
          <thead>
            <tr>
              <th
                style={{ ...s.th, cursor: 'pointer', userSelect: 'none' }}
                onClick={toggleNameSort}
                title="Sort by name"
                aria-sort={nameSort === 'asc' ? 'ascending' : nameSort === 'desc' ? 'descending' : 'none'}
              >
                Name / Email{nameArrow}
              </th>
              <th style={s.th}>Department</th>
              <th style={s.th}>{tableT('hrProfile.team')}</th>
              {canChangeManager && <th style={s.th}>{tableT('hrFollow.lineManager')}</th>}
              <th style={s.th}>Visa</th>
              <th style={s.th}></th>
            </tr>
          </thead>
          <tbody>
            {displayedEmployees.map(emp => (
              <tr key={emp.id} style={{ ...s.tr, opacity: emp.is_archived ? 0.65 : 1 }}>
                <td style={s.td}>
                  <p style={{ margin: '0 0 2px 0', fontWeight: 500, fontSize: '13px', color: '#1a1a2e' }}>
                    {emp.first_name} {emp.last_name}
                    <InternBadge employee={emp} />
                    {emp.is_archived && <span style={{ fontSize: '11px', color: '#aaa', marginLeft: '6px' }}>(former)</span>}
                  </p>
                  <p style={{ margin: 0, fontSize: '12px', color: '#aaa' }}>{emp.company_email}</p>
                </td>
                <td style={s.td}>{emp.department || '-'}</td>
                <td style={s.td}>{emp.function_team || '-'}</td>
                {canChangeManager && (
                  <td style={s.td}>
                    <ManagerCell emp={emp} />
                  </td>
                )}
                <td style={s.td}>
                  {emp.requires_visa
                    ? <span style={s.visaPill}><span style={s.pillDot} />Visa required</span>
                    : <span style={s.noVisaPill}>No visa</span>}
                </td>
                <td style={s.td}>
                  <button style={s.viewBtn} onClick={() => setSelectedEmployee(emp.id)}>
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table></ScrollableTable>
      )}
    </>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────
function Dashboard({ currentUser, topBarIdentity, role, onLogout, hasTechnologyAccess }) {
  // CLVIEW: this shell now serves the COO, admin AND a Country Lead. isCountryLead distinguishes
  // them so a panel never has to infer it from something else.
  //
  // WHAT IT IS FOR: not offering what the server refuses. A payroll account is already 403 on the
  // HR report, policies, retention and every compliance endpoint, so an unfiltered sidebar would
  // be full of dead items. THE SERVER REMAINS THE CONTROL - this changes no permission, and
  // deleting it would make the nav wrong, not insecure.
  const isCountryLead = role === 'country_lead';
  // HRSHELL: this shell now ALSO serves the hr role, for the same reason and in the same shape as
  // the Country Lead above: every screen HR needs already exists here, so the alternative was a
  // second component re-implementing ten sections. isHr drives three things and nothing else -
  // which base path the URLs use, which sidebar entries render, and which actions inside the
  // reused screens are hidden. IT CHANGES NO PERMISSION; the server is still the control.
  const isHr = role === 'hr';
  // The shell's own base path. '/dashboard' for coo/admin/country_lead, so their URLs, redirects,
  // bookmarks and tests are untouched; '/hr' for the HR shell.
  const SHELL_BASE = isHr ? HR_SHELL_ROUTES.base : '/dashboard';
  // The sections the HR shell is allowed to render, taken from the shell declaration itself so
  // the allowlist and the sidebar cannot disagree. 'hr-overview' is added because it is the id
  // the landing page resolves to, and 'employees' because the employee list and the profile deep
  // link both live under it.
  const HR_SECTION_IDS = useMemo(
    () => new Set([...HR_SHELL_ROUTES.sectionIds, 'hr-overview', 'employees']),
    [],
  );
  // HRSHELL: all HR-visible text goes through i18n, EN and FR. Named dashT rather than t to match
  // this file's convention of avoiding a bare `t`, which several callbacks here use as a row
  // argument name and would shadow. The COO nav labels are deliberately NOT converted in this
  // build: they are untouched English literals today and changing them is not in scope.
  const { t: dashT } = useTranslation();
  // HRLABEL: the shell's TITLE and the sidebar's ROLE LINE, derived from the role rather than
  // hardcoded as the COO's. An hr account saw "COO Dashboard" in the top bar and "Chief Operating
  // Officer" under its own name, which is wrong and reads as if the account had been given the
  // COO's access. A Country Lead saw the same two strings, so this was never hr-specific.
  //
  // THE LABELS COME FROM ONE MAP so the two places cannot disagree, and the HR ones go through
  // i18n because they are new HR-visible text. The COO and Country Lead strings are kept as the
  // literals they already were: converting them is not in this task's scope, and changing the
  // COO's own wording is a change nobody asked for.
  const _shellTitle = isHr
    ? dashT('hrNav.shellTitle')
    : isCountryLead ? 'Country Lead' : 'COO Dashboard';
  const _roleLine = isHr
    ? dashT('hrNav.roleLine')
    : isCountryLead ? 'Country Lead' : 'Chief Operating Officer';
  const { isDarkMode } = useDarkMode();
  const s = mkS(isDarkMode);
  // RESPONSIVE Phase 1: the COO sidebar narrows below ~900px (240px -> 200px) instead of its
  // flexShrink:0 crushing the main content. Invisible at normal desktop widths.
  const isNarrow = useIsNarrow();
  const rnav = useNavigate();
  const location = useLocation();
  // HRSHELL: the employee-profile deep link lives under the SHELL'S OWN base, so an hr account
  // opening a profile stays inside /hr and the back button returns to the HR list it came from.
  const empProfileMatch = useMatch(`${SHELL_BASE}/employees/:employeeId`);
  // LEAVERITCOO: /dashboard/employees/<id>/leaver-it, the button of the COO's leaver IT email, opens
  // that leaver's form at the IT section (see the effect after the leaver form state below).
  const leaverItMatch = useMatch(`${SHELL_BASE}/employees/:employeeId/leaver-it`);
  const leaverItId = leaverItMatch?.params?.employeeId || null;

  // ── URL ↔ section maps ────────────────────────────────────────────────────────
  // HRSHELL: both maps are now DERIVED from the shell's base path rather than hardcoding
  // '/dashboard'. For the COO, admin and a Country Lead, SHELL_BASE is '/dashboard' and every
  // entry below is byte-identical to what it was, so no existing URL, redirect, test or bookmark
  // changes. For an hr account SHELL_BASE is '/hr', which is what gives that role real,
  // reloadable, back-button-able URLs for the sections it is allowed to see.
  //
  // THE MAP IS DELIBERATELY NOT NARROWED PER ROLE. It still names every section, including the
  // Finance and Compliance ones, because this map only answers "what URL would this section
  // have". What an hr account may REACH is decided by navSections (which omits them) and, for
  // anyone typing a URL by hand, by the HR_SECTION_IDS allowlist below - and above all by the
  // server, which refuses those endpoints to hr regardless of what the client renders.
  const SECTION_TO_URL = {
    overview: SHELL_BASE, analytics: `${SHELL_BASE}/analytics`, performance: `${SHELL_BASE}/performance`,
    profile: isHr ? `${SHELL_BASE}/my-profile` : `${SHELL_BASE}/profile`,
    tasks: `${SHELL_BASE}/tasks`,
    // For the COO shell 'overview' and 'hr-overview' are two distinct sections. In the HR shell
    // the LANDING page IS HR Overview, so both ids resolve to the bare base and the role sees one
    // Oversight entry rather than two that look the same.
    'hr-overview': isHr ? SHELL_BASE : `${SHELL_BASE}/hr-overview`,
    employees: isHr ? SHELL_BASE : `${SHELL_BASE}/hr-overview`,
    'hr-report': `${SHELL_BASE}/hr-report`, newstarters: `${SHELL_BASE}/new-starters`,
    leavers: `${SHELL_BASE}/leavers`, interns: `${SHELL_BASE}/interns`,
    compliance: `${SHELL_BASE}/compliance`, policies: `${SHELL_BASE}/policies`,
    'compliance-report': `${SHELL_BASE}/compliance-report`, requests: `${SHELL_BASE}/app-requests`,
    grievances: `${SHELL_BASE}/grievances`, retention: `${SHELL_BASE}/retention`,
    workflows: `${SHELL_BASE}/workflows`, validation: `${SHELL_BASE}/validation`,
    leavecal: `${SHELL_BASE}/leave-calendar`,
    absreg: `${SHELL_BASE}/absence-register`,
    'financial-workflows': `${SHELL_BASE}/financial-workflows`,
    'compliance-workflows': `${SHELL_BASE}/compliance-workflows`,
    // TRADEURL: the two trade desk sections had a sidebar item and a screen but no URL, so a
    // click resolved to undefined here and setActiveSection's `if (url)` guard did nothing. Same
    // defect class RETENTIONNAV fixed for Retention Review. Who may OPEN them is still the
    // server's decision (GET /trades/my-desks), applied to typed URLs below.
    'trade-front-office': `${SHELL_BASE}/trade-front-office`,
    'trade-back-office': `${SHELL_BASE}/trade-back-office`,
  };
  const URL_TO_SECTION = {
    [SHELL_BASE]: isHr ? 'hr-overview' : 'overview',
    [`${SHELL_BASE}/analytics`]: 'analytics', [`${SHELL_BASE}/performance`]: 'performance',
    [`${SHELL_BASE}/profile`]: 'profile', [`${SHELL_BASE}/my-profile`]: 'profile',
    [`${SHELL_BASE}/tasks`]: 'tasks', [`${SHELL_BASE}/hr-overview`]: 'hr-overview',
    [`${SHELL_BASE}/hr-report`]: 'hr-report',
    [`${SHELL_BASE}/new-starters`]: 'newstarters', [`${SHELL_BASE}/leavers`]: 'leavers',
    [`${SHELL_BASE}/interns`]: 'interns',
    [`${SHELL_BASE}/compliance`]: 'compliance', [`${SHELL_BASE}/policies`]: 'policies',
    [`${SHELL_BASE}/compliance-report`]: 'compliance-report', [`${SHELL_BASE}/app-requests`]: 'requests',
    [`${SHELL_BASE}/grievances`]: 'grievances', [`${SHELL_BASE}/retention`]: 'retention',
    [`${SHELL_BASE}/workflows`]: 'workflows', [`${SHELL_BASE}/validation`]: 'validation',
    [`${SHELL_BASE}/leave-calendar`]: 'leavecal',
    [`${SHELL_BASE}/absence-register`]: 'absreg',
    [`${SHELL_BASE}/financial-workflows`]: 'financial-workflows',
    [`${SHELL_BASE}/compliance-workflows`]: 'compliance-workflows',
    [`${SHELL_BASE}/trade-front-office`]: 'trade-front-office',
    [`${SHELL_BASE}/trade-back-office`]: 'trade-back-office',
  };
  // LEAVECALHIDE: is the leave calendar reachable at all? Decided by the SERVER
  // (GET /auth/me returns leave_calendar_enabled, default false), for the same reason tradeDesks
  // below is server-decided: the client must not be the control. STARTS FALSE so a slow or failed
  // check never flashes a Leave calendar entry that the API would then 404 - the same
  // "start closed" rule the desks use, and the honest default now that the calendar is hidden.
  const [leaveCalEnabled, setLeaveCalEnabled] = useState(false);
  useEffect(() => {
    let live = true;
    axios.get(`${API}/auth/me`)
      .then((res) => {
        if (!live || !res.data) return;
        setLeaveCalEnabled(Boolean(res.data.leave_calendar_enabled));
      })
      .catch(() => { /* unreachable: leave the calendar hidden */ });
    return () => { live = false; };
  }, []);
  // TRADEURL: declared HERE, above the section resolution, because the typed-URL guard below
  // reads it. Moved unchanged from further down.
  // TRADEFLOW: which trade desk views this account may open, as decided by the SERVER
  // (GET /trades/my-desks returns two booleans and no address). Starts closed, so a slow or failed
  // check never flashes a desk item at somebody who is not on that desk.
  const [tradeDesks, setTradeDesks] = useState({ front_office: false, back_office: false });
  useEffect(() => {
    let live = true;
    axios.get(`${API}/trades/my-desks`)
      .then((res) => {
        if (!live || !res.data) return;
        setTradeDesks({
          front_office: Boolean(res.data.front_office),
          back_office: Boolean(res.data.back_office),
        });
      })
      .catch(() => { /* not on either desk, or not reachable: leave both closed */ });
    return () => { live = false; };
  }, []);
  const _urlSection = empProfileMatch ? 'hr-overview' : (URL_TO_SECTION[location.pathname] || (isHr ? 'hr-overview' : 'overview'));
  // HRSHELL: A TYPED URL CANNOT REACH A SECTION THIS ROLE HAS NO ENTRY FOR. Without this, an hr
  // account that typed /hr/compliance would render the Compliance screen, whose every panel would
  // then 403 - exactly the "a control that fails on click" the task forbids. Falling back to the
  // landing section is the same fail-closed shape the route guard in App.js already uses.
  // Not a security boundary: the server refuses those endpoints to hr whatever the client does.
  const _roleSection = (isHr && !HR_SECTION_IDS.has(_urlSection)) ? 'hr-overview' : _urlSection;
  // LEAVECALHIDE: A TYPED URL CANNOT REACH THE HIDDEN CALENDAR EITHER. /hr/leave-calendar and
  // /dashboard/leave-calendar are still in URL_TO_SECTION (nothing is deleted, so the URLs still
  // resolve when the setting is turned back on), which means without this line a typed URL would
  // leave activeSection on 'leavecal' and render a BLANK section inside the shell. Falling back to
  // the shell's landing section is the same fail-closed shape the HR_SECTION_IDS line above uses,
  // and the same reasoning: a screen this role has no entry for must not be reachable by typing.
  // Not a security boundary - the server 404s the calendar endpoints whatever the client does.
  const _calSection = (_roleSection === 'leavecal' && !leaveCalEnabled)
    ? (isHr ? 'hr-overview' : 'overview')
    : _roleSection;
  // TRADEURL: A TYPED URL CANNOT REACH A TRADE DESK THIS ACCOUNT MAY NOT OPEN, the same
  // fail-closed shape as the calendar line above. tradeDesks starts closed, so until
  // GET /trades/my-desks answers, a desk URL shows the landing section rather than flashing a
  // screen whose API would 403. Not a security boundary: the server refuses the desk itself.
  const _TRADE_DESK_OF = { 'trade-front-office': 'front_office', 'trade-back-office': 'back_office' };
  const activeSection = (_TRADE_DESK_OF[_calSection] && !tradeDesks[_TRADE_DESK_OF[_calSection]])
    ? (isHr ? 'hr-overview' : 'overview')
    : _calSection;
  const setActiveSection = (section) => { const url = SECTION_TO_URL[section]; if (url) rnav(url); };
  const selectedEmployee = empProfileMatch?.params?.employeeId || null;
  const setSelectedEmployee = (id) => { if (id) rnav(`${SHELL_BASE}/employees/${id}`); };
  // TASKDEEPLINK: the task_type a task-list click carried (if any), read from the URL so it
  // survives the route change. Null when the profile is opened without a task (e.g. "View" from
  // the employee list), so EmployeeProfile then behaves exactly as before (no modal/scroll).
  const taskDeepLinkType = new URLSearchParams(location.search).get('task_type') || null;

  // True while EmployeeProfile has the New Starter Form open - used to hide the outer
  // "Employees › {name}" breadcrumb so only the form's own breadcrumb shows.
  const [profileFormOpen, setProfileFormOpen] = useState(false);
  useEffect(() => { setProfileFormOpen(false); }, [selectedEmployee]);

  const [showNotifications, setShowNotifications] = useState(false);
  const [notifFilter, setNotifFilter] = useState('all');
  const [showPendingTasks, setShowPendingTasks] = useState(false);
  const [showVisaSponsored, setShowVisaSponsored] = useState(false);
  const [showStartLeaver, setShowStartLeaver] = useState(false);  // LEAVERUI
  const [dashboard, setDashboard] = useState(null);
  const [employees, setEmployees] = useState([]);
  // LOADERR: which of the page's data loads FAILED (network/server error, not a 404 "no form").
  // The design audit's core finding: a failed GET rendered identically to genuinely-empty data,
  // so "No tasks" could mean "the server is down". Keys are human-readable load names shown in
  // the page-level LoadError banner; sections whose data failed swap their empty state for the
  // error state instead of silently claiming there is nothing.
  const [loadErrors, setLoadErrors] = useState([]);
  // CONFIRMMODAL: styled confirmation replacing native window.confirm (design-audit decision).
  const [askConfirm, confirmModal] = useConfirm(isDarkMode);
  // LEAVERARCHVIS: archived employees, fetched separately (status=former) ONLY for the Leavers
  // "Offboarded" list. Kept out of the main `employees` array on purpose - every other view
  // (lists, counts, new starters) must keep excluding archived people.
  const [formerEmployees, setFormerEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [empSearch, setEmpSearch] = useState('');
  const [empStatus, setEmpStatus] = useState('active');
  const [visaFilter, setVisaFilter] = useState(false);
  // EMPFILTERSORT: client-side country/entity + team filters and name sort, lifted here alongside
  // the existing employee-list controls (EMPTABLESTABLE keeps EmployeeTable a stateless stable
  // function, so all its controls live on Dashboard and are passed down as props).
  const [entityFilter, setEntityFilter] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [nameSort, setNameSort] = useState(null); // null | 'asc' | 'desc'

  // New starter / leaver forms for badge + views
  const [newStarterForms, setNewStarterForms] = useState({});
  const [leaverForms, setLeaverForms] = useState({});

  // Leaver form full-page view
  const [leaverEmployee, setLeaverEmployee] = useState(null);

  // Analytics drill-down state
  const [analyticsStatus, setAnalyticsStatus] = useState(null);
  const [analyticsFn, setAnalyticsFn] = useState(null);
  const [analyticsTask, setAnalyticsTask] = useState(null);
  const [analyticsWorkflow, setAnalyticsWorkflow] = useState(null);
  const [hoveredWf, setHoveredWf] = useState(null);
  const [upcomingDeadlines, setUpcomingDeadlines] = useState([]);
  const [functionData, setFunctionData] = useState([]);
  const [leaverFormSection, setLeaverFormSection] = useState(1);
  // LEAVERITCOO: open the leaver form at Section 2 (IT), then make the URL the employee's profile, so
  // Back and a refresh land on the profile rather than reopening the form. Not for the HR shell,
  // which cannot fill the IT section.
  useEffect(() => {
    if (!leaverItId || isHr) return;
    setLeaverFormSection(2);
    setLeaverEmployee(leaverItId);
    rnav(`${SHELL_BASE}/employees/${leaverItId}`, { replace: true });
  }, [leaverItId]); // eslint-disable-line react-hooks/exhaustive-deps
  // VALALWAYSVIS: is_manager/is_approver state removed - the COO Dashboard's Validation item is now
  // always visible (coo/admin is the permanent fallback approver), so this shell needs only the
  // pending-approval count for the badge, never the manager booleans.
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);
  // HRSHELL: the manager/approver booleans, needed again but ONLY for the HR shell. VALALWAYSVIS
  // removed them because the COO's Validation item is unconditional; that reasoning is specific to
  // coo/admin being the permanent fallback approver and does not extend to hr, whose Validation
  // entry must appear "only when the account is a manager or approver, as today" - today being
  // StaffView, which gated its Validation button on exactly these two booleans from the same
  // endpoint. Fetched only when isHr, so the COO shell makes no extra request.
  const [isManager, setIsManager] = useState(false);
  const [isApprover, setIsApprover] = useState(false);
  // HRSHELL point 4: the hr account's OWN tasks, from GET /dashboard/my-tasks, which already
  // admits hr and filters server side on the caller's own employee id. Distinct from the COO's
  // `operational_tasks`, which is an organisation-wide list from GET /dashboard/ - that one is
  // "every task in the company" and is not what "the tasks assigned to the hr account" means.
  const [myTasks, setMyTasks] = useState([]);
  const [myTasksLoading, setMyTasksLoading] = useState(false);
  const [myTasksError, setMyTasksError] = useState(false);
  const myTasksOverdueCount = myTasks.filter((mt) => mt.is_overdue).length;
  // Named fetcher, not an inline effect body, so the LoadError retry can re-run the REAL request
  // rather than a neighbouring one - the same shape FinanceView's fetchMyTasks already uses.
  const fetchMyTasks = React.useCallback(() => {
    setMyTasksLoading(true);
    axios.get(`${API}/dashboard/my-tasks`)
      .then((r) => { setMyTasks(r.data?.tasks || []); setMyTasksError(false); })
      .catch(() => { setMyTasks([]); setMyTasksError(true); })
      .finally(() => setMyTasksLoading(false));
  }, []);
  // APPREQASSIGN item 7: the App requests nav count. Same mechanism as pendingApprovalCount right
  // above (a count fetched into state, attached to the nav item as `badge`, rendered only when > 0
  // by the shared renderBadge) - no parallel badge mechanism invented. For this shell the number
  // is the COO's own decision queue; see the navSections entry for the full reasoning.
  const [appRequestCount, setAppRequestCount] = useState(0);

  // ADMINCOMPLETE: the two ADMINDELAY Admin leaver tasks are the only tasks in this list with no
  // completion route of their own - no document to upload and no form section to submit, so none
  // of the existing affordances close them, and they sat pending forever once dispatched. This
  // wires a "Mark complete" to the EXISTING generic endpoint and adds no backend behaviour.
  // It does NOT touch ADMINDELAY's dispatch, its checkbox-triggers-dispatch semantics, or its
  // dedup guard, and it is deliberately NOT BENEFITTASK's derive-from-checkbox model: there the
  // checkbox is the source of truth, here the checkbox means "dispatch this item" and the task
  // is separate work owned by someone else. Completing the task never writes the checkbox.
  const ADMIN_COMPLETABLE_TASKS = ['leaver_signatory_removal', 'leaver_org_chart_update'];
  // INTERNOFFBOARDHR (2026-10-21): intern offboarding is HR's task now, and the COO keeps it in his
  // Operational Tasks. It has no document to upload, so both get "Mark complete" on it, through the
  // same generic endpoint (which admits coo, admin and hr). One task row: done for one is done for both.
  // PENSIONFC point 4: the UK payroll contact's enrolment tasks, which the COO and admin may
  // complete too. No document is required, so the same generic endpoint closes them.
  const OPS_COMPLETABLE_TASKS = [...ADMIN_COMPLETABLE_TASKS, 'intern_offboard',
    'new_starter_pension_enrol', 'new_starter_health_insurance_enrol'];
  const [completingMyTaskId, setCompletingMyTaskId] = useState(null);
  const [myTaskCompleteError, setMyTaskCompleteError] = useState(false);
  const [completingTaskId, setCompletingTaskId] = useState(null);
  const [taskCompleteError, setTaskCompleteError] = useState(null);
  // TASKTABS (confirmed decision by Marie-Lynne): Operational Tasks shows overdue and upcoming as
  // two TABS rather than two stacked sections. Defaults to OVERDUE - the already-late work should
  // not sit behind a click - and both counts live on the tab labels so picking one never hides the
  // existence of the other. Component state, so it survives a refetch and a retry but resets on a
  // full page reload, matching every other tab pair in this codebase. Deliberately NOT in the URL:
  // it is a preference within an already-routed section, and the two overdue-only cards elsewhere
  // on this dashboard are untouched by it.
  const [opsTasksTab, setOpsTasksTab] = useState('overdue');

  // INTERNOFFBOARDHR: HR's own list refreshes itself, not the COO's dashboard payload.
  const markMyTaskComplete = async (taskId) => {
    setCompletingMyTaskId(taskId);
    setMyTaskCompleteError(false);
    try {
      await axios.patch(`${API}/dashboard/tasks/${taskId}/complete`);
      fetchMyTasks();
    } catch (err) {
      setMyTaskCompleteError(fpStepRefusal(err.response?.data?.detail, dashT) || true);
    }
    setCompletingMyTaskId(null);
  };

  // WFTASKS: a workflow step task completes its step; a step that needs a file or a form opens.
  const markWorkflowStepTaskComplete = async (task) => {
    setCompletingTaskId(task.task_id);
    setTaskCompleteError(null);
    try {
      const outcome = await completeWorkflowStepTask(API, task, (path) => rnav(path));
      if (outcome === 'completed') await fetchData();
    } catch (err) {
      setTaskCompleteError(err.response?.data?.detail || dashT('workflowStepTask.error'));
    }
    setCompletingTaskId(null);
  };

  const markTaskComplete = async (taskId) => {
    setCompletingTaskId(taskId);
    setTaskCompleteError(null);
    try {
      await axios.patch(`${API}/dashboard/tasks/${taskId}/complete`);
      await fetchData();
    } catch (err) {
      setTaskCompleteError(fpStepRefusal(err.response?.data?.detail, dashT)
        || err.response?.data?.detail || 'Could not complete this task.');
    }
    setCompletingTaskId(null);
  };

  const fetchData = async () => {
    // LOADERR: every fallback below now RECORDS which load failed instead of silently
    // pretending the data was empty. A 404 on a per-employee form fetch is the normal
    // "no form exists" answer and is NOT a failure; anything else is.
    // SECFIX-S2-NSPAY-UX: a 403 is likewise NOT a failure - it is the expected, correct
    // access restriction once NEW_STARTER_PAYROLL_SCOPE_RELEASED excludes the coo from
    // GET /new-starter/{id} (see submit_section3/get_new_starter_form's NSPAYSCOPE gate,
    // new_starter.py). Surfacing "Some data failed to load... Retry" for a 403 is misleading -
    // retrying changes nothing, and nothing is actually broken. Only a genuine error (500,
    // network/timeout, or any other status) is still recorded.
    const errs = new Set();
    const formFail = (key) => (err) => {
      const status = err?.response?.status;
      if (status !== 404 && status !== 403) errs.add(key);
    };
    try {
      const [dashRes, empRes, deadlinesRes, funcRes, formerRes] = await Promise.all([
        axios.get(`${API}/dashboard/`).catch(() => { errs.add('dashboard summary'); return { data: null }; }),
        axios.get(`${API}/employees/`),
        // HRVISAALL: HR's Overview shows the VISA deadlines the COO sees (visa and right to work
        // expiry), so HR asks for those types only; the COO's call is unchanged.
        axios.get(`${API}/dashboard/upcoming-deadlines${isHr ? '?visa_only=true' : ''}`).catch(() => { errs.add('upcoming deadlines'); return { data: { upcoming_deadlines: [] } }; }),
        axios.get(`${API}/performance/overdue-by-function`).catch(() => { errs.add('performance data'); return { data: [] }; }),
        // LEAVERARCHVIS: archived employees for the Offboarded leavers list only.
        axios.get(`${API}/employees/?status=former`).catch(() => { errs.add('archived employees'); return { data: [] }; }),
      ]);
      setDashboard(dashRes.data);
      setUpcomingDeadlines(deadlinesRes.data.upcoming_deadlines || []);
      setFunctionData(funcRes.data || []);
      const allEmps = empRes.data;
      setEmployees(allEmps);
      const formerEmps = formerRes.data || [];
      setFormerEmployees(formerEmps);
      // Load new starter and leaver forms for all employees (for badge counts + views)
      const nsMap = {};
      const lvMap = {};
      await Promise.all(allEmps.map(emp =>
        Promise.all([
          axios.get(`${API}/new-starter/${emp.id}`).then(r => { nsMap[emp.id] = r.data; }).catch(formFail('onboarding forms')),
          axios.get(`${API}/leaver/${emp.id}`).then(r => { lvMap[emp.id] = r.data; }).catch(formFail('leaver forms'))
        ])
      ));
      // LEAVERARCHVIS: leaver forms for the archived employees too - the Offboarded list reads
      // last_working_date/reason from this same map. New-starter forms are not needed for them.
      // LEAVERBULK: ONE grouped request instead of one GET per archived employee (N+1). Falls
      // back to the previous per-employee loop if the bulk endpoint is unavailable (e.g. the
      // frontend deployed ahead of the backend).
      if (formerEmps.length > 0) {
        try {
          const bulkRes = await axios.post(`${API}/leaver/bulk`, { employee_ids: formerEmps.map(e => e.id) });
          Object.assign(lvMap, bulkRes.data.forms || {});
        } catch (e) {
          await Promise.all(formerEmps.map(emp =>
            axios.get(`${API}/leaver/${emp.id}`).then(r => { lvMap[emp.id] = r.data; }).catch(formFail('leaver forms'))
          ));
        }
      }
      setNewStarterForms(nsMap);
      setLeaverForms(lvMap);
    } catch (err) {
      // Only the unguarded GET /employees/ can land here - the page can't render without it.
      errs.add('employee list');
      console.error(err);
    } finally {
      setLoadErrors(Array.from(errs));
      setLoading(false);
    }
  };

  const navigateToAnalytics = (statusFilter) => {
    setAnalyticsStatus(statusFilter || null);
    setAnalyticsFn(null);
    setAnalyticsTask(null);
    setAnalyticsWorkflow(null);
    setActiveSection('analytics');
  };

  const navigate = (section, url, opts = {}) => {
    if (section === 'analytics') {
      setAnalyticsStatus(opts.status || null);
      setAnalyticsFn(opts.fn || null);
      setAnalyticsTask(null);
      setAnalyticsWorkflow(opts.workflow || null);
    }
    if (section === 'employees') {
      setVisaFilter(opts.visaFilter || false);
    }
    setActiveSection(section);
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line

  // "Validation" pending-approval count + deep-link routing (VALALWAYSVIS). The item itself is
  // always shown in this shell now, so this effect only needs the count for the badge and to honor
  // an emailed ?next=validation deep link - it no longer reads is_manager/is_approver.
  useEffect(() => {
    // VALALWAYSVIS: the Validation item is now ALWAYS shown in this (coo/admin) shell and its
    // deep-link routing is unconditional, so this shell no longer needs is_manager/is_approver at
    // all - it only needs the pending-approval COUNT for the badge. Fetched unconditionally; the
    // endpoint is self-scoped server-side and an empty result just yields no badge. (The prior
    // GET /employees/me read here only fed the now-removed manager-gate and is dropped.)
    axios.get(`${API}/leave/pending-approvals`)
      .then(r => setPendingApprovalCount((r.data || []).length))
      .catch(() => setPendingApprovalCount(0));
    // HRSHELL: the HR shell's Validation entry appears only for a manager or approver, which is
    // what StaffView did and what the task asks to keep. coo/admin skip this request entirely,
    // so their shell is unchanged and makes no extra call.
    if (isHr) {
      fetchMyTasks();
      axios.get(`${API}/employees/me`)
        .then((r) => {
          setIsManager(Boolean(r.data?.is_manager));
          setIsApprover(Boolean(r.data?.is_approver));
        })
        // Fails closed: an account whose record cannot be read is not offered Validation. This is
        // also the no-employee-record case, where the whole endpoint 404s (point 7's warning).
        .catch(() => { setIsManager(false); setIsApprover(false); });
    }
    // VALALWAYSVIS: honor an emailed deep link's ?next=validation UNCONDITIONALLY in this shell,
    // since Validation is always reachable here now. (Previously gated on is_manager/is_approver,
    // which could drop the signal for a COO with zero pending at click time. The approval
    // PERMISSION is still enforced server-side per declaration, so routing here is safe.)
    //
    // FPFIX: the signal is PEEKED first and consumed only when it is 'validation'. Consuming it
    // unconditionally cleared 'attestations' and 'fp' before the branch below could read them, so
    // the COO, HR and the Country Lead stayed on this home instead of reaching My Profile.
    const _next = peekPostLoginNext();
    if (_next === 'validation') { consumePostLoginNext(); setActiveSection('validation'); }
    // ATTESTDEEPLINK: an attestation email's ?next=attestations targets the personal My Profile
    // view (EmployeeView), not this admin dashboard - whose Compliance section is intentionally
    // read-only oversight and does not load the COO's own attestations. So route to My Profile
    // and let EmployeeView consume the signal and open its Attestations tab. PEEK (not consume)
    // here so EmployeeView still sees it. This is exactly the Craig case: a dual-role account
    // whose default landing hid their own pending attestation.
    else if (['attestations', 'fp'].includes(_next)) setActiveSection('profile');
  }, []); // eslint-disable-line

  // APPREQASSIGN item 7: the App requests count. Fetched on mount and refetched whenever the
  // active section changes, so approving or KO-ing something and navigating away updates the badge
  // without a page reload. The endpoint is role-aware server-side and returns 0 for anyone with no
  // such nav entry, so no client-side role gate is needed here.
  useEffect(() => {
    axios.get(`${API}/application-requests/pending-count`)
      .then(r => setAppRequestCount(r.data?.count || 0))
      .catch(() => setAppRequestCount(0));
  }, [activeSection]);

  const fetchEmployees = async (status, search) => {
    const params = new URLSearchParams({ status });
    if (search?.trim()) params.set('search', search.trim());
    try {
      const empRes = await axios.get(`${API}/employees/?${params}`);
      setEmployees(empRes.data);
      // LOADERR: a successful refetch clears a previous employee-list failure.
      setLoadErrors(prev => prev.filter(k => k !== 'employee list'));
    } catch (err) {
      console.error(err);
      // LOADERR: surface the failure instead of leaving a stale/empty list unexplained.
      setLoadErrors(prev => (prev.includes('employee list') ? prev : [...prev, 'employee list']));
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => fetchEmployees(empStatus, empSearch), 300);
    return () => clearTimeout(timer);
  }, [empSearch, empStatus]); // eslint-disable-line

  const openNotif = (filter) => { setNotifFilter(filter); setShowNotifications(true); };

  if (loading) return <div style={s.loading}>Loading Enko Portal...</div>;
  if (!dashboard) return <div style={s.loading}>Error loading dashboard</div>;

  // VISACARD: visa_alert_tasks comes from GET /dashboard/ (HRVISA), already server-side
  // scoped and carrying its own actor and assigned_to_me flags. Defaulted to [] because
  // an older cached payload, or a deployment where the backend has not restarted yet,
  // would not have the key at all - and a crash on an absent key would take the whole
  // Tasks section with it rather than just the new block.
  const { summary, overdue_tasks, overdue_tasks_hr, operational_tasks,
          visa_alert_tasks = [] } = dashboard;

  // VISACARD point 2's ordering: OVERDUE FIRST, then by due date. Sorted on a COPY, because
  // Array.prototype.sort mutates and `visa_alert_tasks` is the payload object's own array - the
  // server already returns it due-date ascending, and re-sorting it in place would be an
  // invisible side effect on shared state.
  //
  // A NULL due_date sorts LAST within its group rather than first. The server flags such a task
  // is_overdue=false (there is no date to have passed), so it belongs with the upcoming work, and
  // treating a missing date as "due now" would push an undated task above a genuinely urgent one.
  const visaFollowUp = [...visa_alert_tasks].sort((a, b) => {
    if (a.is_overdue !== b.is_overdue) return a.is_overdue ? -1 : 1;
    if (!a.due_date) return b.due_date ? 1 : 0;
    if (!b.due_date) return -1;
    return a.due_date.localeCompare(b.due_date);
  });

  // Derived data for sidebar badges + views
  const activeEmps = employees.filter(e => e.is_active !== false);
  // THE LIST's membership, deliberately UNCHANGED by NSCOUNT. Everyone with an incomplete form
  // stays visible on the New starters screen, including a migrated or existing employee: that
  // screen is how HR chases outstanding onboarding, and Dashboard.accesswait.test.js CAW-4 exists
  // to assert that the wait indicator "adds a badge, it hides nobody". The COUNT is the thing that
  // was wrong, and it is computed separately below.
  const newStarterInProgress = activeEmps.filter(e =>
    !e.is_archived && newStarterForms[e.id]?.status !== 'complete'
  );

  // NSCOUNT (2026-10-16): THE BADGE COUNTS REAL NEW STARTERS ONLY, not the headcount.
  //
  // THE DEFECT THIS FIXES. The badge used newStarterInProgress.length, whose filter asks nothing
  // about whether the person CAME THROUGH THE NEW STARTER PATH. Every employee has a
  // NewStarterForm row, and update_overall_status (new_starter.py:205) only advances past
  // "pending" when section1_completed is true - which itself requires BOTH Part A and Part B
  // (new_starter.py:202). On the dev database section 1 is complete for ZERO employees, so every
  // form sat at "pending" and the badge printed the entire headcount. A badge equal to the
  // headcount carries no information: it cannot distinguish a busy week from a quiet one.
  //
  // THE RULE, built from EXISTING FIELDS ONLY and from the distinction the portal already
  // documents at employees.py:193-203 (the same two flags, no third column):
  //   * a genuine new joiner  -> is_migrated falsy AND migration_confirmed falsy  (COUNTED)
  //   * an existing employee  -> migration_confirmed TRUE                         (excluded)
  //   * a migrated employee   -> is_migrated TRUE                                 (excluded)
  // plus: must have a form at all, be active, not archived, have no leaver process, and the form
  // must not be complete.
  //
  // SEPARATE FROM THE LIST, by decision. Applying this to the list as well would have removed a
  // migrated employee from the screen HR uses to chase onboarding, which is a different and
  // larger change than fixing a number.
  //
  // THE LEAVER CHECK READS leaverForms DIRECTLY, and that detail cost a correction. My first
  // version tested `e._leaverInProgress`, copying line 220 - but that property is attached at
  // line 1356, for a DIFFERENT component that receives pre-tagged employees. The raw rows here
  // carry no such field, so the condition would have been permanently true and the leaver
  // exclusion silently absent. `leaverForms` is the real state, in scope, and the same map
  // _leaverStarted and leaversDraft already read.
  //
  // WHY NO RECENCY WINDOW, which was offered and declined. A 2026-06 starter who never finished
  // onboarding is still an incomplete new starter, and hiding them after N months would make the
  // badge smaller by CONCEALING the stale onboarding this count exists to surface.
  const newStarterCounted = activeEmps.filter(e =>
    !e.is_archived
    && !e.is_migrated
    && !e.migration_confirmed
    && !leaverForms[e.id]
    && !!newStarterForms[e.id]
    && newStarterForms[e.id].status !== 'complete'
  );
  // LEAVERUI: three derived groups for the redesigned Leavers page.
  //  - started        = a leaver process exists (section1 completed), same signal the old
  //                     "In Progress" list and the old candidate-list complement used.
  //  - leaversOffboarded = the MAIN list now: all 3 sections complete = the person is fully
  //                     offboarded. This is exactly the condition that fires the leaver emails
  //                     (_maybe_send_all_leaver_emails requires all 3), so it is the correct
  //                     "completed/offboarded" definition and needs no backend change.
  //  - leaversInProgress = started but NOT yet all-3-complete: still surfaced (secondary section)
  //                     so people mid-process are never hidden - only the not-yet-started list is
  //                     removed. Purely display grouping; no data/logic change.
  const _leaverStarted = e => !!leaverForms[e.id]?.section1_completed;
  const _leaverComplete = e => {
    const lv = leaverForms[e.id];
    return !!(lv?.section1_completed && lv?.section2_completed && lv?.section3_completed);
  };
  // LEAVERARCHVIS: Offboarded now includes ARCHIVED employees whose leaver form is complete -
  // archiving is the normal final step of the process, and previously it made the person vanish
  // from this list entirely (the main `employees` fetch excludes is_archived). Union of the
  // active + former fetches, deduped by id as a safety guard. In Progress stays on `employees`
  // only: a non-archived in-progress leaver is already kept in the active fetch by the backend's
  // OR branch, and archiving is blocked while active workflow assignments exist (WFARCHIVEGATE).
  const leaversOffboarded = [...employees, ...formerEmployees]
    .filter((e, i, arr) => arr.findIndex(x => x.id === e.id) === i)
    .filter(_leaverComplete);
  const leaversInProgress = employees.filter(e => _leaverStarted(e) && !_leaverComplete(e));
  // LEAVERDRAFT: a leaver form row EXISTS but Section 1 was only saved as a draft, never
  // submitted (POST /leaver/{id}/section1?draft=true creates the row without section1_completed).
  // Distinct from "never started", which has no leaverForm row at all. This is a NEW category:
  // "In Progress" keeps meaning exactly section1_completed=true (confirmed COO decision).
  const leaversDraft = employees.filter(e => !!leaverForms[e.id] && !leaverForms[e.id].section1_completed);
  // LOADERR: when the leaver-form or archived-employee loads failed, an empty leaver card must
  // say SO, not "No leavers" - the audit's error-vs-empty conflation, fixed at the source lists.
  const leaverDataFailed = loadErrors.includes('leaver forms') || loadErrors.includes('archived employees') || loadErrors.includes('employee list');

  // INTERNVIEW: active interns (employee_type='intern', not archived), sorted by end date ascending
  // so the soonest/overdue departures come first. INTERNENDSHARED: the "ended" decision and the days
  // count now come from the ONE shared timezone-safe helper (internship.js), identical to the logic
  // EmployeeProfile's CTA and the backend job use, so the badge here can never disagree with the
  // profile on the boundary day. `_end` (the raw YYYY-MM-DD, or null) is kept only for sorting.
  // Interns with no end date sort last (a real gap the COO can then fill from the row).
  const activeInterns = employees
    .filter(e => e.employee_type === 'intern' && !e.is_archived)
    .map(e => {
      const end = endDateYMD(e.internship_end_date);
      return {
        ...e,
        _end: end,
        _daysRemaining: internshipDaysRemaining(e.internship_end_date),
        _ended: isInternshipEnded(e.internship_end_date),
      };
    })
    .sort((a, b) => {
      if (!a._end && !b._end) return 0;
      if (!a._end) return 1;   // no end date -> last
      if (!b._end) return -1;
      return a._end < b._end ? -1 : a._end > b._end ? 1 : 0;  // soonest first (YYYY-MM-DD sorts chronologically)
    });
  const internsEnded = activeInterns.filter(i => i._ended);

  // Sidebar profile name
  const myRecord = employees.find(e => e.company_email.toLowerCase() === currentUser.toLowerCase());
  const displayName = myRecord ? `${myRecord.first_name} ${myRecord.last_name}` : currentUser;
  const avatarInitial = myRecord ? myRecord.first_name.charAt(0) : currentUser.charAt(0).toUpperCase();

  // My Profile: fresh email lookup + back button via CooProfileView
  if (activeSection === 'profile') {
    return (
      <CooProfileView
        email={currentUser}
        currentUser={currentUser}
        onLogout={onLogout}
        role={role}
        onBack={() => setActiveSection('overview')}
      />
    );
  }

  // UX audit item 8: FINANCE ("Workflows" alone) and OTHER ("App requests" alone) were
  // single-item groups - ad-hoc placement that only added visual sidebar clutter without
  // organizing anything. Folded into MAIN/COMPLIANCE respectively (same ids/handlers/badges,
  // purely a group-membership change, zero functional change) until either grows a second
  // item that would actually justify its own labeled section again.
  // HRSHELL point 2: the HR sidebar, declared here rather than in a second component so every
  // entry reuses the COO's existing section render block unchanged.
  //
  // THREE GROUPS, exactly as the task specifies: MAIN, HR, and Policy Repository on its own.
  // NO FINANCE, PAYROLL OR COMPLIANCE GROUP, and no Analytics or Performance (see the reasoning
  // recorded against each in shellRoutes.js's HR_SHELL_ROUTES comment).
  //
  // VALIDATION KEEPS TODAY'S RULE, not the COO's. The COO shell shows Validation unconditionally
  // (VALALWAYSVIS) because coo/admin are the permanent fallback approver for anyone with no
  // manager. That reasoning does not extend to hr, and the task says "only when the account is a
  // manager or approver, as today" - which is what StaffView did, so the behaviour an existing HR
  // user sees is unchanged.
  const hrNavSections = [
    { label: dashT('hrNav.main'), items: [
      { id: 'overview',   label: dashT('hrNav.oversight'),  icon: <IconDashboard /> },
      { id: 'profile',    label: dashT('hrNav.myProfile'),  icon: <IconUser /> },
      { id: 'tasks',      label: dashT('hrNav.tasks'), icon: <IconCheck />,
        badge: myTasksOverdueCount || null },
      // HRAPPREQ: App requests, which every other role's shell already offers and HR's did not.
      //
      // FOR HR THIS IS "MY OWN REQUESTS", NOT THE COO'S DECISION QUEUE, and that is a property of
      // the server rather than a choice made here: GET /application-requests/ filters to
      // `submitted_by == current_user.email` for any caller who is not technology-capable
      // (application_requests.py:395), and hr is not. So an hr account sees the requests it
      // raised itself, exactly as an ordinary employee does from the employee shell.
      //
      // NO BADGE, deliberately. The COO's badge counts "decisions blocked on me", which hr cannot
      // make, so the same number would be meaningless here and a zero-count badge would imply
      // there was a queue to clear. The section shows its own state when opened.
      { id: 'requests', label: dashT('hrNav.appRequests'), icon: <IconTools /> },
      // FPFIX: always present for HR, manager or not: HR's F&P step is on the Validation page.
      // The badge still counts only HR's own pending leave decisions.
      { id: 'validation', label: dashT('hrNav.validation'), icon: <IconCheck />,
        badge: ((isManager || isApprover) && pendingApprovalCount) || null },
    ]},
    { label: dashT('hrNav.hr'), items: [
      { id: 'hr-report',   label: dashT('hrNav.hrReport'),    icon: <IconChart /> },
      { id: 'newstarters', label: dashT('hrNav.newStarters'), icon: <IconUserPlus />,
        badge: newStarterCounted.length || null },
      { id: 'interns',     label: dashT('hrNav.interns'),      icon: <IconUser />,
        badge: internsEnded.length || null },
      { id: 'leavers',     label: dashT('hrNav.leavers'),      icon: <IconUserMinus /> },
      { id: 'grievances',  label: dashT('hrNav.grievances'), icon: <IconShield /> },
      // LEAVECALHIDE: the entry is present only when the setting is on. Spread rather than
      // a ternary returning null, so nothing renders in its place when hidden.
      ...(leaveCalEnabled
        ? [{ id: 'leavecal',    label: dashT('hrNav.leaveCalendar'), icon: <IconUsers /> }]
        : []),
      { id: 'absreg',      label: dashT('absenceRegister.navLabel'), icon: <IconFiles /> },
    ]},
    { label: dashT('hrNav.policies'), items: [
      { id: 'policies',    label: dashT('hrNav.policyRepository'), icon: <IconFiles /> },
    ]},
  ];

  const navSections = isHr ? hrNavSections : [
    { label: 'MAIN', items: [
      { id: 'overview',   label: 'Oversight',  icon: <IconDashboard /> },
      { id: 'analytics',   label: 'Analytics',   icon: <IconChart /> },
      // CLREAD part 2 (SUPERSEDES CLVIEW's suppression): Performance is offered to a Country Lead
      // again. CLVIEW hid it because all six endpoints were organisation-wide and the router
      // refused payroll; both halves changed in part 2 - every endpoint now recomputes its
      // aggregates for the caller's entity and the router admits a Country Lead. The panel is
      // read-only for everyone, so there is no action control to hide here.
      { id: 'performance', label: 'Performance', icon: <IconTrend /> },
      { id: 'profile',     label: 'My Profile',  icon: <IconUser /> },
      // TASKBADGEOVERDUE: already overdue-only, but from the WRONG source. `overdue_tasks` is a
      // SEPARATE response field built by a `due_before=now` query, which is `due_date < now` and so
      // EXCLUDES a task with no due date - while the Overdue TAB splits on the server's `is_overdue`,
      // which counts a null due_date AS overdue. The two agree on today's dev data only because no
      // pending task currently has a null due_date (measured: 0 of 26 for the COO). The moment one
      // does, the badge would have been one short of the tab it is meant to summarise.
      //
      // Now derived from the SAME array and the SAME field the tab uses, so they cannot diverge.
      // `overdue_tasks` itself is UNTOUCHED and still feeds the two genuinely overdue-only cards
      // (Top 5 urgent, HR Overview), which this build does not change.
      { id: 'tasks',      label: 'Tasks',      icon: <IconCheck />,   badge: operational_tasks.filter(t => t.is_overdue).length || null },
      // APPREQASSIGN item 7: a count, attached the SAME way every other badge in this nav is
      // (a `badge` property on the item, rendered by the shared renderBadge only when > 0). For
      // the COO the number means: requests waiting on THEIR decision - submitted/under_review
      // awaiting approve-or-reject, plus in_testing awaiting live-or-KO. Deliberately NOT the same
      // set a technology account counts (which is what is assigned to them and still buildable):
      // the COO is the only role that can make those decisions, so their badge answers "how many
      // decisions are blocked on me", which is a different and equally correct number.
      { id: 'requests', label: 'App requests', icon: <IconTools />, badge: appRequestCount || null },
      // "Validation" - VALALWAYSVIS: ALWAYS visible in the COO Dashboard, even with zero pending
      // approvals, per the confirmed COO decision. This shell is only ever reached by coo/admin,
      // who are the PERMANENT fallback approver for any employee with no manager_id
      // (_resolve_approver_id), so the item is always relevant here - the page shows its own empty
      // state when nothing is pending. The badge still shows a number ONLY when > 0 (|| null), so a
      // zero-pending state renders the item with no badge, matching the other badge conventions.
      // The isManager/isApprover approval PERMISSION is unchanged; this only affects nav visibility.
      { id: 'validation', label: 'Validation', icon: <IconCheck />, badge: pendingApprovalCount || null },
      // COOWORKFLOWSPLIT (supersedes FINLOCK): FINLOCK hid this item from a real COO because the
      // workflows module was not ready to be shown - that reason no longer applies, the COO is
      // now asked to review workflows, so this is no longer gated on isTestAccount(). This MAIN
      // entry shows only the instances the COO is personally involved in (scope='involved') -
      // the same involvement rule every other role's own view already uses. The FINANCE and
      // COMPLIANCE sections below add the two category-wide views; all three open the same
      // WorkflowList component with a different server-side scope, never a client-side filter.
      { id: 'workflows', label: 'Workflows', icon: <IconFiles /> },
    ]},
    { label: 'HR', items: [
      // CLREAD part 2 (SUPERSEDES CLVIEW's suppression): both are offered to a Country Lead again.
      // HR Overview reads GET /dashboard/, which CLVIEW already scoped to the caller's entity, plus
      // the two Oversight panels that part 2 scoped and opened. HR Report reads /hr/report/*, whose
      // seven endpoints are now entity-scoped with the gate opened in the same change. Neither
      // screen carries an action control: HR Report's two buttons only navigate to the employees
      // list, which is itself entity-scoped server side.
      { id: 'hr-overview', label: 'HR Overview',  icon: <IconDashboard /> },
      { id: 'hr-report',   label: 'HR Report',    icon: <IconChart /> },
      { id: 'newstarters', label: 'New starters', icon: <IconUserPlus />, badge: newStarterCounted.length || null },
      // INTERNVIEW: dedicated Active Interns view, COO/admin only. Placed in HR beside New starters
      // and Leavers - the same employee-lifecycle group - reusing the existing section mechanism.
      // The badge shows how many interns have already ended (past end date), so an overdue offboard
      // is visible from the sidebar; null when none, matching the other count badges here.
      ...((role === 'coo' || role === 'admin') ? [{ id: 'interns', label: 'Interns', icon: <IconUser />, badge: internsEnded.length || null }] : []),
      { id: 'leavers',     label: 'Leavers',      icon: <IconUserMinus /> },
      // CLREAD part 2: GRIEVANCES STAY CLOSED, deliberately unchanged from CLVIEW. Not an
      // oversight in this build: GrievanceReport has NO entity column and an anonymous report has
      // no submitter to join through, so there is nothing to scope it by even if the decision
      // changed. No grievance gate admits payroll either (verify_clread R-7 asserts both).
      ...(isCountryLead ? [] : [{ id: 'grievances', label: 'Grievances', icon: <IconShield /> }]),
      // ABSREG point 2: the COO shell had NO Leave calendar entry at all - the section and its
      // render block already existed and the server already scopes the data by role, so the
      // entry was the only missing piece. Same id, same component and same global view as the
      // HR shell's entry, so the two shells show one calendar rather than two variants.
      //
      // ABSREG point 3: the absence register, beside it, in the HR group of both shells.
      // A COUNTRY LEAD IS REFUSED THE REGISTER BY THE SERVER (hr/coo/admin only, point 3f),
      // so the entry is hidden for one rather than offered as a control that 403s on click -
      // the same fail-closed shape the Grievances line above already uses. The calendar is
      // NOT hidden from them, because the server genuinely scopes it to their own entity.
      // LEAVECALHIDE: hidden unless the setting is on, same rule as the HR shell above.
      ...(leaveCalEnabled
        ? [{ id: 'leavecal',    label: 'Leave calendar', icon: <IconUsers /> }]
        : []),
      // POLICYHR: the register entry uses the SERVER'S allowlist for the same reason the
      // policy mount below does. This was written as `!isCountryLead` one build earlier and
      // is latent rather than broken - only coo, admin, hr and country_lead reach this array
      // today, and all four happen to agree - but it is the identical shape that put an
      // upload button in front of hr, so it is corrected here rather than left to surface.
      ...(ABSENCE_REGISTER_ROLES.includes(role)
        ? [{ id: 'absreg', label: 'Absence register', icon: <IconFiles /> }]
        : []),
    ]},
    // CLREAD part 2 (PARTIALLY SUPERSEDES CLVIEW's blanket suppression): a Country Lead gets the
    // COMPLIANCE group back, but only the items whose endpoints part 1 actually opened. CLVIEW hid
    // the whole group on the then-true grounds that every endpoint behind it refused payroll; that
    // is no longer true of two of the five, and is STILL true of the other three.
    //
    // RESTORED, because part 1 opened these endpoints to a Country Lead, entity-scoped:
    //   Compliance Report -> /compliance/report/* (4 endpoints)
    //   Compliance        -> the five declaration lists + the registers and their CSV exports
    // Both mount read-only for them: ComplianceModule already receives readOnly, and its
    // canChangeStatus / canSeeEmployeeDetail flags both require isStaffView with a
    // compliance-or-coo/admin role, which a Country Lead ('country_lead', no isStaffView) is not -
    // so every Approve/Reject/Amend/Cease control and the per-employee tab stay hidden.
    //
    // Policies is restored READ-ONLY: GET /policies/ and /{id}/view are auth-only for every
    // account, but all three WRITES (upload, PATCH, DELETE) are compliance/admin/coo, so canManage
    // is false for a Country Lead and the upload/edit/delete controls do not render.
    //
    // STILL SUPPRESSED, because the server still refuses them and part 2 does not open them:
    //   Retention Review      -> /retention/flags is require_roles("compliance","coo","admin")
    //   Compliance workflows  -> the workflows gates do not admit a Country Lead
    ...(isCountryLead
      ? [{ label: 'COMPLIANCE', items: [
          { id: 'compliance-report', label: 'Compliance Report', icon: <IconChart /> },
          { id: 'compliance',        label: 'Compliance',        icon: <IconShield /> },
          { id: 'policies',          label: 'Policies',          icon: <IconFiles /> },
        ]}]
      : [
    { label: 'COMPLIANCE', items: [
      { id: 'compliance-report', label: 'Compliance Report', icon: <IconChart /> },
      { id: 'compliance',        label: 'Compliance',        icon: <IconShield /> },
      { id: 'policies',          label: 'Policies',          icon: <IconFiles /> },
      { id: 'retention',         label: 'Retention Review',  icon: <IconShield /> },
      // COOWORKFLOWSPLIT: the compliance-category slice of Workflows (scope='compliance'),
      // subject to the same fp_assessment carve-out the backend enforces - every compliance-type
      // instance EXCEPT fp_assessment is shown blanket, and fp_assessment only where the COO is
      // personally involved (e.g. the current review_assessment assignee). No other Compliance
      // item on this page is affected; this is purely a new entry pointing at WorkflowList.
      { id: 'compliance-workflows', label: 'Compliance workflows', icon: <IconFiles /> },
    ]},
    ]),
    // COOWORKFLOWSPLIT (supersedes FINRESTORE/FINLOCK): FINANCE is now unconditional for every
    // coo/admin, not gated on isTestAccount() - FINLOCK's reason (module not ready to show the
    // COO) no longer applies, per the confirmed COO decision superseding it. The item opens the
    // same WorkflowList component as MAIN's "Workflows" and COMPLIANCE's "Compliance workflows",
    // scoped server-side to scope='finance' (every finance-category instance, blanket, matching
    // the Financial Controller's own existing blanket-finance view in App.js).
    { label: 'FINANCE', items: [
      { id: 'financial-workflows', label: 'Financial workflows', icon: <IconFiles /> },
    ]},
    // TRADEFLOW: each desk item appears only for an account the SERVER says may open it
    // (GET /trades/my-desks), so the navigation never offers a view that would 403. coo and admin
    // are included by that same endpoint, so no role test is duplicated here - and the GROUP itself
    // appears only when at least one item will, so nobody is shown an empty heading.
    ...((tradeDesks.front_office || tradeDesks.back_office)
      ? [{ label: 'OPERATIONS', items: [
          ...(tradeDesks.front_office
            ? [{ id: 'trade-front-office', label: 'Trade actions (FO)', icon: <IconFiles /> }] : []),
          ...(tradeDesks.back_office
            ? [{ id: 'trade-back-office', label: 'Trade actions (BO)', icon: <IconFiles /> }] : []),
        ]}]
      : []),
  ];

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', background: isDarkMode ? '#0f1a2e' : '#FAFAF8' }}>

      {/* ── Modals / Overlays ── */}
      {showPendingTasks && <PendingTasksModal onClose={() => setShowPendingTasks(false)} onSelectEmployee={id => setSelectedEmployee(id)} />}
      {showVisaSponsored && <VisaSponsoredModal onClose={() => setShowVisaSponsored(false)} onSelectEmployee={id => setSelectedEmployee(id)} employees={employees} />}
      {/* LEAVERUI: selecting someone here calls setLeaverEmployee(id) - the identical start action
          the old "Start Leaver" row button used, so the leaver flow is unchanged. Each employee is
          tagged with _leaverInProgress so the modal excludes those already started, matching the
          old candidate list's eligibility exactly. */}
      {showStartLeaver && (
        <StartLeaverModal
          onClose={() => setShowStartLeaver(false)}
          onSelectEmployee={id => setLeaverEmployee(id)}
          // LEAVERDRAFT: a row's mere existence (draft OR submitted) now disqualifies the
          // employee from a fresh start - resuming happens from the Draft list / the profile.
          employees={employees.map(e => ({ ...e, _leaverInProgress: !!leaverForms[e.id] }))}
        />
      )}
      {showNotifications && <Notifications onClose={() => setShowNotifications(false)} onSelectEmployee={id => { setShowNotifications(false); setSelectedEmployee(id); }}
        onSelectTask={(id, taskType, task) => {
          setShowNotifications(false);
          // WFTASKS: a workflow step task opens that workflow step.
          if (isWorkflowStepTask(task)) { rnav(workflowStepOpenPath(task)); return; }
          rnav(`${SHELL_BASE}/employees/${id}?task_type=${encodeURIComponent(taskType)}`);
        }}
        filter={notifFilter} currentUser={currentUser} />}
      {confirmModal}
      {showAddEmployee && <AddEmployee onClose={() => setShowAddEmployee(false)} onCreated={() => { setShowAddEmployee(false); fetchData(); }} currentUserRole={role} />}

      {/* ── Top nav ── */}
      <div style={s.topNav}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }} onClick={() => {
            // leaverEmployee is plain local state, not route-derived (unlike selectedEmployee,
            // which comes from useMatch and clears itself automatically on navigation) - the
            // inline Leaver Form view it drives (below) is checked before any route-based
            // rendering, so without this it would keep rendering after this click navigates
            // away. Mirrors the exact clear-then-navigate pairing the sidebar nav buttons
            // already use (line ~636).
            setLeaverEmployee(null);
            setLeaverFormSection(1);
            rnav(ROLE_DEFAULTS[(role || 'employee').toLowerCase()] || '/dashboard');
          }} title="Go to dashboard">
          <span style={s.sigma}>Σ</span>
          <div>
            <span style={s.portalLabel}>Enko Portal</span>
            <span style={s.dashLabel}> · {_shellTitle}</span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {/* TOPBARNAME: name, falling back to the email for an account with no employee record. */}
          <span style={s.navEmail}>{topBarIdentity || currentUser}</span>
          {/* ABSREG point 4: the absence notification bell, in the top bar of the HR and COO
              shells. Rendered for exactly the roles the server lets read the register and the
              notifications (hr/coo/admin, point 3f): a Country Lead reaches this same Dashboard
              component, and showing them a bell whose every call 403s would be a control that
              fails on click. The count itself comes from the server, per user (point 4e).
              Clicking a notification opens the register section (point 4d). */}
          {(role === 'hr' || role === 'coo' || role === 'admin') && (
            <NotificationBell
              dark={isDarkMode}
              onOpenNotification={() => setActiveSection('absreg')}
            />
          )}
          <DarkModeToggle />
          {/* SECONDARYROLE: an ORDINARY navigation link to a route this account is genuinely,
              permanently, database-authorized to reach (per ProtectedRoute's secondary_role check),
              not a session-scoped switch/override. It only renders for the one account this real
              second role was set on. */}
          {hasTechnologyAccess && (
            <TopbarBtn onClick={() => rnav('/it')}>Technology shell</TopbarBtn>
          )}
          <button style={s.goldBtn} onClick={() => setShowAddEmployee(true)}>+ Add employee</button>
          <TopbarBtn onClick={onLogout}>Sign out</TopbarBtn>
        </div>
      </div>

      {/* ── Body ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>

        {/* ── Sidebar ── (RESPONSIVE Phase 3: shared <Sidebar>; behaviour byte-identical - same
            profile block, divider, navSections, s.navItem/navItemActive/navSectionLabel tokens, the
            s.goldBadge shown only when item.badge > 0, and the SAME onClick that clears the leaver
            state before routing. Same responsive width (240 -> 200 narrow). s.sidebar's own width is
            overridden by the Sidebar's width prop, matching the previous inline override exactly.) */}
        <Sidebar
          width={isNarrow ? '200px' : '240px'}
          containerStyle={s.sidebar}
          profile={(
            <div style={s.sidebarProfile}>
              <div style={s.sidebarAvatar}>{avatarInitial}</div>
              <div style={{ minWidth: 0 }}>
                <p style={s.sidebarName}>{displayName}</p>
                <p style={s.sidebarRole}>{_roleLine}</p>
              </div>
            </div>
          )}
          dividerAfterProfile={<div style={s.sidebarDivider} />}
          sections={navSections}
          activeSection={activeSection}
          onSelect={(item) => {
            setLeaverEmployee(null);
            setLeaverFormSection(1);
            if (item.action) item.action();
            else setActiveSection(item.id);
          }}
          itemStyle={s.navItem}
          itemActiveStyle={s.navItemActive}
          sectionLabelStyle={s.navSectionLabel}
          renderBadge={(item) => item.badge > 0 ? <span style={s.goldBadge}>{item.badge}</span> : null}
        />

        {/* ── Main content ── */}
        <div style={s.main}>

          {leaverEmployee ? (
            <>
              {(() => {
                const emp = employees.find(e => e.id === leaverEmployee) || {};
                const empName = [emp.first_name, emp.last_name].filter(Boolean).join(' ') || '…';
                return (
                  <div style={{ marginBottom: '16px' }}>
                    <span
                      style={{ fontSize: '13px', color: '#9ba8c0', cursor: 'pointer', transition: 'color 0.12s' }}
                      onMouseEnter={e => e.currentTarget.style.color = '#c9a84c'}
                      onMouseLeave={e => e.currentTarget.style.color = '#9ba8c0'}
                      onClick={() => { setLeaverEmployee(null); setLeaverFormSection(1); rnav(SECTION_TO_URL['hr-overview']); }}
                    >Employees</span>
                    <span style={{ fontSize: '13px', color: '#9ba8c0', margin: '0 6px' }}>›</span>
                    <span
                      style={{ fontSize: '13px', color: '#9ba8c0', cursor: 'pointer', transition: 'color 0.12s' }}
                      onMouseEnter={e => e.currentTarget.style.color = '#c9a84c'}
                      onMouseLeave={e => e.currentTarget.style.color = '#9ba8c0'}
                      onClick={() => { const id = leaverEmployee; setLeaverEmployee(null); setLeaverFormSection(1); rnav(`${SHELL_BASE}/employees/${id}`); }}
                    >{empName}</span>
                    <span style={{ fontSize: '13px', color: '#9ba8c0', margin: '0 6px' }}>›</span>
                    <span style={{ fontSize: '13px', color: '#1a2744', fontWeight: 500 }}>Leaver Form</span>
                  </div>
                );
              })()}
              <LeaverForm
                employeeId={leaverEmployee}
                employeeName={[employees.find(e => e.id === leaverEmployee)?.first_name, employees.find(e => e.id === leaverEmployee)?.last_name].filter(Boolean).join(' ')}
                requiresVisa={employees.find(e => e.id === leaverEmployee)?.requires_visa}
                location={employees.find(e => e.id === leaverEmployee)?.location}
                section={leaverFormSection}
                // HRLEAVER (2026-10-22) SUPERSEDES HRPAYFIX point 9: hr completes Section 3 in
                // full, as the COO does, and the server admits it. hr completes Sections 1 and 3;
                // the IT section (2) stays with the COO, as new starter Section 2 does.
                allowedSections={isHr ? [1, 3] : undefined}
                completedBy={currentUser}
                onBack={() => { setLeaverEmployee(null); setLeaverFormSection(1); fetchData(); }}
                onNavigate={(n) => setLeaverFormSection(n)}
                onComplete={() => { setLeaverEmployee(null); setLeaverFormSection(1); fetchData(); }}
              />
            </>
          ) : selectedEmployee ? (
            <>
              {!profileFormOpen && (
                <div style={{ marginBottom: '16px' }}>
                  <span
                    style={{ fontSize: '13px', color: '#9ba8c0', cursor: 'pointer' }}
                    onMouseOver={e => { e.currentTarget.style.color = '#c9a84c'; }}
                    onMouseOut={e => { e.currentTarget.style.color = '#9ba8c0'; }}
                    onClick={() => { fetchData(); rnav(SECTION_TO_URL['hr-overview']); }}
                  >
                    Employees
                  </span>
                  <span style={{ fontSize: '13px', color: '#9ba8c0', margin: '0 6px' }}>›</span>
                  <span style={{ fontSize: '13px', color: '#1a2744', fontWeight: 500 }}>
                    {(() => { const emp = employees.find(x => x.id === selectedEmployee); return emp ? `${emp.first_name} ${emp.last_name}` : '…'; })()}
                  </span>
                </div>
              )}
              <EmployeeProfile
                employeeId={selectedEmployee}
                taskDeepLinkType={taskDeepLinkType}
                currentUser={currentUser}
                role={role}
                onBack={() => { fetchData(); rnav(SECTION_TO_URL['hr-overview']); }}
                onNavigateToNewStarters={() => { rnav(SECTION_TO_URL.newstarters); }}
                onNavigateToEmployees={() => { fetchData(); rnav(SECTION_TO_URL['hr-overview']); }}
                onFormOpenChange={setProfileFormOpen}
              />
            </>
          ) : (
            <>

          {/* LOADERR: page-level read-failure banner - names exactly which loads failed so no
              list below can silently pass off a server failure as "you have nothing". Retry
              re-runs the full fetch. Rendered above whichever section is active. */}
          {loadErrors.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <LoadError
                dark={isDarkMode}
                message={`Some data failed to load (${loadErrors.join(', ')}) - the lists below may be incomplete.`}
                onRetry={fetchData}
              />
            </div>
          )}

          {/* ── OVERVIEW (main dashboard) ── */}
          {activeSection === 'overview' && (
            <div style={{ margin: '-20px -24px', padding: '24px 24px 0', background: isDarkMode ? '#0f1a2e' : '#edecea' }}>

              {/* ROW 1 - 4 KPI cards */}
              <div style={{ ...s.metricsRow, gridTemplateColumns: gridCols(isNarrow, 'repeat(4, 1fr)') }}>
                <KpiCard label="Overdue"                value={summary.overdue_tasks}           accent={TASK_COLOURS.overdue}    sub="need immediate action"  onClick={() => navigateToAnalytics('Overdue')} />
                <KpiCard label="Pending"                value={summary.pending_tasks}           accent={TASK_COLOURS.pending}    sub="in progress"             onClick={() => navigateToAnalytics('Pending')} />
                <KpiCard label="Completed this month"   value={summary.completed_this_month ?? 0} accent={TASK_COLOURS.doneOnTime} sub="on time or late"        onClick={() => { setAnalyticsStatus(null); setAnalyticsFn(null); setAnalyticsTask(null); setActiveSection('analytics'); }} />
                <KpiCard label="New starters in progress" value={newStarterCounted.length} accent="#1a2744" sub="onboarding incomplete"    onClick={() => setActiveSection('newstarters')} />
              </div>

              {/* ROW 2 - left 2/3 + right 1/3 */}
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px', alignItems: 'start' }}>

                {/* LEFT COLUMN */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                  {/* Card A - Top 5 urgent */}
                  <div style={{ ...s.card, marginBottom: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                      <h3 style={{ ...s.cardTitle, margin: 0, color: '#1a2744' }}>Top 5 urgent</h3>
                      <button style={{ fontSize: '12px', color: TASK_COLOURS.overdue, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500 }}
                        onClick={() => navigateToAnalytics('Overdue')}>View all →</button>
                    </div>
                    {overdue_tasks.length === 0 ? (
                      <p style={s.empty}>No overdue tasks.</p>
                    ) : overdue_tasks.slice(0, 5).map((item, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '7px 10px', marginBottom: '6px', borderLeft: `3px solid ${TASK_COLOURS.overdue}`, backgroundColor: '#fdf5f5', borderRadius: '6px' }}>
                        <span style={{ fontWeight: 600, fontSize: '13px', color: '#1a2744', flexShrink: 0, marginRight: '8px' }}>{item.employee_name}</span>
                        <span style={{ flex: 1, fontSize: '13px', color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.task_title}</span>
                        <span style={{ fontSize: '12px', color: TASK_COLOURS.overdue, fontWeight: 600, marginLeft: '8px', flexShrink: 0 }}>{item.days_overdue}d</span>
                      </div>
                    ))}
                  </div>

                </div>

                {/* RIGHT COLUMN */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                  {/* Card C - Functions */}
                  <div style={{ ...s.card, marginBottom: 0 }}>
                    <h3 style={{ ...s.cardTitle, color: '#1a2744' }}>Functions</h3>
                    {functionData.length === 0 ? (
                      <p style={s.empty}>No data available yet.</p>
                    ) : (() => {
                      const maxFn = Math.max(...functionData.map(f => f.count));
                      return functionData.map(func => (
                        <div key={func.function} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '9px', cursor: 'pointer' }}
                          onClick={() => setActiveSection('analytics')}>
                          <div style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, backgroundColor: TASK_COLOURS.overdue }} />
                          <span style={{ fontSize: '13px', color: '#1a2744', fontWeight: 500, flex: '0 0 76px' }}>{func.function}</span>
                          <div style={{ flex: 1, height: '8px', backgroundColor: '#e8e4dc', borderRadius: '4px', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${(func.count / maxFn) * 100}%`, backgroundColor: TASK_COLOURS.overdue }} />
                          </div>
                          <span style={{ fontSize: '11px', color: '#aaa', flexShrink: 0, minWidth: '18px', textAlign: 'right' }}>{func.count}</span>
                        </div>
                      ));
                    })()}
                  </div>

                  {/* Card E - Upcoming deadlines */}
                  <div style={{ ...s.card, marginBottom: 0 }}>
                    <h3 style={{ ...s.cardTitle, color: '#1a2744' }}>Upcoming deadlines</h3>
                    {upcomingDeadlines.length === 0 ? (
                      <p style={s.empty}>No upcoming deadlines.</p>
                    ) : upcomingDeadlines.map((item, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '7px 10px', marginBottom: '6px', borderLeft: `3px solid ${TASK_COLOURS.pending}`, backgroundColor: '#fdf8ee', borderRadius: '6px' }}>
                        <span style={{ fontWeight: 600, fontSize: '13px', color: '#1a2744', flexShrink: 0, marginRight: '8px' }}>{item.employee_name}</span>
                        <span style={{ flex: 1, fontSize: '13px', color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.description}</span>
                        <span style={{ fontSize: '12px', color: TASK_COLOURS.pending, fontWeight: 600, marginLeft: '8px', flexShrink: 0 }}>
                          {item.days_remaining >= 30 ? `${Math.floor(item.days_remaining / 30)} months` : `${item.days_remaining}d`}
                        </span>
                      </div>
                    ))}
                  </div>

                </div>

              </div>
            </div>
          )}

          {/* ── HR OVERVIEW ── */}
          {activeSection === 'hr-overview' && (
            <>
              {/* 4 metric cards */}
              <div style={{ ...s.metricsRow, gridTemplateColumns: gridCols(isNarrow, 'repeat(4, 1fr)') }}>
                {[
                  { label: 'Total Employees', value: summary.total_employees, onClick: () => setActiveSection('employees') },
                  // REGOLD point 2a (2026-10-16): the `overdue` flag KEPT from the removal build.
                  // It replaced a `gold: true` flag that TWO cards carried (Overdue Tasks and
                  // Visa Sponsored), so keying the red on that flag would have turned the visa
                  // count red as well. Only overdue goes red, which is what the decision says.
                  { label: 'Overdue Tasks',   value: summary.overdue_tasks,   overdue: true, onClick: () => openNotif('overdue') },
                  { label: 'Pending Tasks',   value: summary.pending_tasks,   onClick: () => setShowPendingTasks(true) },
                  { label: 'Visa Sponsored',  value: summary.visa_employees,  onClick: () => setShowVisaSponsored(true) },
                ].map(({ label, value, overdue, onClick }) => (
                  <div key={label} style={{ ...s.metricCard, cursor: 'pointer' }} onClick={onClick}>
                    <p style={s.metricLabel}>{label}</p>
                    {/* REGOLD point 2a: the ONE place the gold does NOT come back. Stat card
                        numbers are NAVY, the colour of the top bar, and OVERDUE takes a MUTED RED
                        when it is above zero and navy at zero - so a red number always means real
                        overdue work and never merely that the card exists. Every other accent in
                        this file is restored. */}
                    <p style={{ ...s.metricValue,
                                color: (overdue && Number(value) > 0) ? '#A63A3A' : '#1a1a2e' }}>{value}</p>
                  </div>
                ))}
              </div>

              {/* HRVISAALL: the upcoming VISA deadlines the COO sees on his Overview (Card E), for HR.
                  Read only, like the rest of this section. */}
              <div style={s.card} data-testid="hr-visa-deadlines">
                <h3 style={s.cardTitle}>{dashT('visaHr.deadlinesTitle')}</h3>
                {upcomingDeadlines.length === 0 ? (
                  <p style={s.empty}>{dashT('visaHr.deadlinesEmpty')}</p>
                ) : upcomingDeadlines.map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '7px 10px', marginBottom: '6px', borderLeft: `3px solid ${TASK_COLOURS.pending}`, backgroundColor: isDarkMode ? 'transparent' : '#fdf8ee', borderRadius: '6px' }}>
                    <span style={{ fontWeight: 600, fontSize: '13px', flexShrink: 0, marginRight: '8px' }}>{item.employee_name}</span>
                    <span style={{ flex: 1, fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.type ? dashT(`visaHr.deadlineType.${item.type}`) : item.description}
                    </span>
                    <span style={{ fontSize: '12px', color: TASK_COLOURS.pending, fontWeight: 600, marginLeft: '8px', flexShrink: 0 }}>
                      {dashT('visaHr.daysRemaining', { count: item.days_remaining })}
                    </span>
                  </div>
                ))}
              </div>

              {/* Two-column: employees + overdue tasks */}
              <div style={{ ...s.twoCol, gridTemplateColumns: gridCols(isNarrow, '1fr 1fr') }}>
                <div style={s.card}>
                  <h3 style={s.cardTitle}>Employees</h3>
                  <EmployeeTable
                    employees={employees} empSearch={empSearch} setEmpSearch={setEmpSearch}
                    empStatus={empStatus} setEmpStatus={setEmpStatus}
                    visaFilter={visaFilter} setVisaFilter={setVisaFilter}
                    entityFilter={entityFilter} setEntityFilter={setEntityFilter}
                    teamFilter={teamFilter} setTeamFilter={setTeamFilter}
                    deptFilter={deptFilter} setDeptFilter={setDeptFilter}
                    nameSort={nameSort} setNameSort={setNameSort}
                    isDarkMode={isDarkMode} s={s} setSelectedEmployee={setSelectedEmployee}
                    canChangeManager={role === 'coo' || role === 'hr'}
                  />
                </div>

                <div style={s.card}>
                  <h3 style={s.cardTitle}>
                    Overdue Tasks
                    {overdue_tasks_hr.length > 0 && <span style={s.redBadge}>{overdue_tasks_hr.length}</span>}
                  </h3>
                  {overdue_tasks_hr.length === 0 ? <p style={s.empty}>No overdue tasks</p>
                    : overdue_tasks_hr.map(task => (
                      <div key={task.task_id} style={s.overdueRow}
                        onClick={() => (isWorkflowStepTask(task)
                          ? rnav(workflowStepOpenPath(task))   /* WFTASKS: opens the workflow step */
                          : rnav(`${SHELL_BASE}/employees/${task.employee_id}?task_type=${encodeURIComponent(task.task_type || '')}`))}>
                        <div style={s.redDot} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={s.overdueEmp}>{task.employee_name}</p>
                          <p style={s.overdueTask}>{task.task_title}</p>
                        </div>
                        <span style={s.overdueDays}><span style={s.pillDot} />{task.days_overdue}d over</span>
                      </div>
                    ))
                  }
                </div>
              </div>
            </>
          )}

          {/* ── TASKS ── */}
          {/* Confirmed product decision: Operational Tasks is the COO's full pending to-do list
              for the role, not just an overdue alert - so it now reads from operational_tasks
              (no due_date filter, sorted nearest-due first by the backend), not overdue_tasks.
              overdue_tasks (role="coo") still feeds the Overview tab's "Top 5 urgent" card above
              and the sidebar Tasks badge; overdue_tasks_hr (role="hr") separately feeds the HR
              Overview tab's "Overdue Tasks" card below - OVERDUESCOPE split these two apart since
              both used to read the same unscoped org-wide list. Both remain deliberately
              overdue-only. */}
          {/* HRSHELL point 4: the HR shell's Tasks section shows THE TASKS ASSIGNED TO THE HR
              ACCOUNT, from GET /dashboard/my-tasks, which already admits hr and filters server
              side on the caller's own employee id.
              NOT the COO's "Operational Tasks" block below it, which renders `operational_tasks`
              from GET /dashboard/ - an organisation-wide list of every pending task in the
              company. Showing that to HR would be a different feature from the one asked for, and
              a much broader one. The COO's block is untouched. */}
          {/* OPSTASKSALL: HR's Tasks open the COO's Operational Tasks card, on HR's OWN tasks (GET
              /dashboard/my-tasks, filtered server side on the caller), in English or French. An upload
              task still opens the profile on its upload; intern offboarding keeps Mark complete. */}
          {activeSection === 'tasks' && isHr && (
            myTasksLoading ? (
              <div style={s.card}><p style={s.empty}>{dashT('hrNav.loading')}</p></div>
            ) : myTasksError ? (
              <div style={s.card}>
                <LoadError dark={isDarkMode} message={dashT('hrNav.myTasksError')} onRetry={fetchMyTasks} />
              </div>
            ) : (
              <OperationalTasksCard
                tasks={myTasks} s={s} isDarkMode={isDarkMode} t={dashT}
                tab={opsTasksTab} onTabChange={setOpsTasksTab}
                error={myTaskCompleteError === true ? dashT('hrNav.markCompleteError') : (myTaskCompleteError || null)}
                renderTitle={(mt) => (
                  /* FPHRVALIDATION: an F&P review task opens its assessment on the Validation page. */
                  fpAssessmentOfTask(mt) ? (
                    <button type="button" data-testid={`mytask-open-${mt.task_type}`}
                      onClick={() => { setOpenFpAssessment(fpAssessmentOfTask(mt)); rnav(SECTION_TO_URL.validation); }}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                               color: 'inherit', font: 'inherit', fontWeight: 500, textAlign: 'left', textDecoration: 'underline' }}>
                      {mt.title}
                    </button>
                  ) : /* HRPROFILE follow-up: an upload task opens the profile on its upload dialog. */
                  OPENABLE_UPLOAD_TASKS.has(mt.task_type) && mt.employee_id ? (
                    <button type="button" data-testid={`mytask-open-${mt.task_type}`}
                      onClick={() => rnav(`${SHELL_BASE}/employees/${mt.employee_id}?task_type=${encodeURIComponent(mt.task_type)}`)}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                               color: 'inherit', font: 'inherit', fontWeight: 500, textAlign: 'left', textDecoration: 'underline' }}>
                      {mt.title}
                    </button>
                  ) : (
                    <span style={{ fontWeight: 500, fontSize: '13px', color: isDarkMode ? '#e8e4d8' : '#1a1a2e' }}>{mt.title}</span>
                  )
                )}
                renderActions={(mt) => (
                  <>
                  {/* INTERNOFFBOARDHR: the one HR task with no upload to close it. */}
                  {mt.task_type === 'intern_offboard' && (
                    <button
                      type="button"
                      disabled={completingMyTaskId === mt.task_id}
                      onClick={() => markMyTaskComplete(mt.task_id)}
                      style={{
                        background: 'none', border: `1px solid ${GOLD_500}`, borderRadius: '6px',
                        padding: '4px 10px', color: GOLD_500, fontSize: '11px', fontWeight: 600,
                        cursor: completingMyTaskId === mt.task_id ? 'default' : 'pointer',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {completingMyTaskId === mt.task_id
                        ? dashT('hrNav.markCompleteSaving') : dashT('hrNav.markComplete')}
                    </button>
                  )}
                  </>
                )}
              />
            )
          )}

          {/* VISACARD (closes HRVISA's open item): the visa follow-up block, BELOW HR's own
              tasks and inside the SAME Tasks section - no new sidebar entry, as asked.

              NOT RENDERED FOR THE COO (point 4): he already sees these tasks as his own, in
              Operational Tasks below, so a second copy would be noise. The `isHr` guard is what
              does that, and it also keeps the block away from every other role - the employee and
              Country Lead shells are different components entirely and never render this one.

              READ-ONLY UNLESS THE TASK IS HERS (point 3). There is no complete control anywhere in
              this block for a task somebody else owns, and the decision is the server's
              `assigned_to_me`, not a client-side id comparison. HR's own tasks card above is
              likewise read-only, so this matches the section it sits in. */}
          {activeSection === 'tasks' && isHr && (
            <div style={s.card}>
              <h3 style={s.cardTitle}>
                {dashT('hrNav.visaFollowUpTitle')}
                {visaFollowUp.filter((v) => v.is_overdue).length > 0 && (
                  <span style={s.redBadge}>
                    {visaFollowUp.filter((v) => v.is_overdue).length}
                  </span>
                )}
              </h3>
              {visaFollowUp.length === 0 ? (
                <p style={s.empty}>{dashT('hrNav.visaFollowUpEmpty')}</p>
              ) : (
                <ScrollableTable>
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={s.th}>{dashT('hrNav.colTask')}</th>
                        <th style={s.th}>{dashT('hrNav.colEmployee')}</th>
                        <th style={s.th}>{dashT('hrNav.colDueDate')}</th>
                        <th style={s.th}>{dashT('hrNav.colToDoBy')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visaFollowUp.map((v) => (
                        <tr key={v.task_id}>
                          <td style={s.td}>{v.task_title}</td>
                          <td style={s.td}>
                            {/* The link to the employee record, which hr may already open
                                (/hr/employees/:id renders EmployeeProfile for this shell). */}
                            <span
                              role="link"
                              tabIndex={0}
                              style={{ color: '#1a2744', cursor: 'pointer' }}
                              onClick={() => rnav(`${SHELL_BASE}/employees/${v.employee_id}`)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  rnav(`${SHELL_BASE}/employees/${v.employee_id}`);
                                }
                              }}
                            >
                              {v.employee_name || '-'}
                            </span>
                          </td>
                          <td style={s.td}>
                            {v.due_date ? v.due_date.slice(0, 10) : '-'}
                            {v.is_overdue && (
                              <span style={{ color: '#A63A3A', fontWeight: 600, marginLeft: 6 }}>
                                {dashT('hrNav.statusOverdue')}
                              </span>
                            )}
                          </td>
                          <td style={s.td}>
                            {/* The "To do by" label, UNCHANGED (point 2): the same vocabulary and
                                the same i18n keys the employee profile panel uses, resolved by the
                                server through TASKACTOR. An unresolvable actor renders the shared
                                Unassigned string rather than a guess. */}
                            {dashT('tasks.toDoBy')}: {v.actor
                              ? dashT(`tasks.actor.${v.actor}`)
                              : dashT('tasks.actorUnknown')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollableTable>
              )}
            </div>
          )}

          {/* OPSTASKSALL: the COO's Operational Tasks card, now the shared OperationalTasksCard, with
              exactly the COO's list, destinations, buttons and English texts (no translator passed).
              The Country Lead reaches this same branch with its own operational_tasks, as before. */}
          {activeSection === 'tasks' && !isHr && (
            <OperationalTasksCard
              tasks={operational_tasks} s={s} isDarkMode={isDarkMode}
              tab={opsTasksTab} onTabChange={setOpsTasksTab}
              error={taskCompleteError}
              onRowClick={(task) => () => {
                // holiday_approval has no approve/reject control on the employee
                // profile - it lives on the Validation page instead. Every other
                // task_type keeps the existing employee-profile destination unchanged.
                if (task.task_type === 'holiday_approval') { rnav(SECTION_TO_URL.validation); return; }
                // WFTASKS: a workflow step task opens that workflow step.
                if (isWorkflowStepTask(task)) { rnav(workflowStepOpenPath(task)); return; }
                // TASKDEEPLINK: carry the clicked task_type as a query param so
                // EmployeeProfile can deep-link to the exact action (open the upload
                // modal / scroll to the relevant card) instead of landing on the generic
                // profile top. Navigation target (the employee profile) is unchanged.
                rnav(`${SHELL_BASE}/employees/${task.employee_id}?task_type=${encodeURIComponent(task.task_type || '')}`);
              }}
              renderActions={(task) => (
                <>
                {/* ADMINCOMPLETE: stopPropagation so completing does not also navigate
                    to the employee profile via the row's own onClick. */}
                {OPS_COMPLETABLE_TASKS.includes(task.task_type) && (
                  <button
                    type="button"
                    disabled={completingTaskId === task.task_id}
                    onClick={(e) => { e.stopPropagation(); markTaskComplete(task.task_id); }}
                    title={task.task_type === 'intern_offboard'
                      ? dashT('hrNav.confirmOffboarded')
                      : task.task_type.endsWith('_enrol')
                        ? dashT('enrolmentTask.confirmDone', 'Confirm the enrolment has been arranged')
                        : 'Confirm this Admin action has been completed'}
                    style={{
                      background: 'none', border: `1px solid ${GOLD_500}`, borderRadius: '6px',
                      padding: '4px 10px', color: GOLD_500, fontSize: '11px', fontWeight: 600,
                      cursor: completingTaskId === task.task_id ? 'default' : 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {completingTaskId === task.task_id ? 'Saving...' : 'Mark complete'}
                  </button>
                )}
                {/* WFTASKS: a workflow step task completes its step, or opens it when the step
                    needs a file or a form. */}
                {isWorkflowStepTask(task) && (
                  <button
                    type="button"
                    data-testid="ops-complete-workflow-step"
                    disabled={completingTaskId === task.task_id}
                    onClick={(e) => { e.stopPropagation(); markWorkflowStepTaskComplete(task); }}
                    title={dashT('workflowStepTask.confirmDone')}
                    style={{
                      background: 'none', border: `1px solid ${GOLD_500}`, borderRadius: '6px',
                      padding: '4px 10px', color: GOLD_500, fontSize: '11px', fontWeight: 600,
                      cursor: completingTaskId === task.task_id ? 'default' : 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {completingTaskId === task.task_id
                      ? dashT('workflowStepTask.saving') : dashT('workflowStepTask.markComplete')}
                  </button>
                )}
                </>
              )}
            />
          )}

          {/* ── EMPLOYEES ── */}
          {activeSection === 'employees' && (
            <div style={s.card}>
              <h3 style={s.cardTitle}>Employees</h3>
              <EmployeeTable
                    employees={employees} empSearch={empSearch} setEmpSearch={setEmpSearch}
                    empStatus={empStatus} setEmpStatus={setEmpStatus}
                    visaFilter={visaFilter} setVisaFilter={setVisaFilter}
                    entityFilter={entityFilter} setEntityFilter={setEntityFilter}
                    teamFilter={teamFilter} setTeamFilter={setTeamFilter}
                    deptFilter={deptFilter} setDeptFilter={setDeptFilter}
                    nameSort={nameSort} setNameSort={setNameSort}
                    isDarkMode={isDarkMode} s={s} setSelectedEmployee={setSelectedEmployee}
                    canChangeManager={role === 'coo' || role === 'hr'}
                  />
            </div>
          )}

          {/* ── NEW STARTERS ── */}
          {activeSection === 'newstarters' && (
            <div style={s.card}>
              <h3 style={s.cardTitle}>
                New Starters: In Progress
                {newStarterInProgress.length > 0 && <span style={s.goldBadge}>{newStarterInProgress.length}</span>}
              </h3>
              {newStarterInProgress.length === 0 ? <p style={s.empty}>No in-progress new starter forms</p> : (
                <ScrollableTable><table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>Employee</th>
                      <th style={s.th}>S1 Employee</th>
                      <th style={s.th}>S2 IT</th>
                      <th style={s.th}>S3 HR</th>
                      <th style={s.th}>S{_s4abText('section4ab.short4a', currentLang())}</th>
                      <th style={s.th}>S{_s4abText('section4ab.short4b', currentLang())}</th>
                      <th style={s.th}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {newStarterInProgress.map(emp => {
                      const ns = newStarterForms[emp.id];
                      const hideSections = emp.is_migrated && !emp.migration_confirmed;
                      return (
                        <tr key={emp.id} style={s.tr}>
                          <td style={s.td}>
                            <p style={{ margin: '0 0 2px 0', fontWeight: 500, fontSize: '13px' }}>{emp.first_name} {emp.last_name}<InternBadge employee={emp} /><AccessWait employee={emp} form={ns} /></p>
                            <p style={{ margin: 0, fontSize: '11px', color: '#aaa' }}>{emp.company_email}</p>
                          </td>
                          {/* S4AB: Section 4 shows as its two steps, 4A and 4B. */}
                          {[ns?.section1_completed, ns?.section2_completed, ns?.section3_completed, s4aDone(ns), s4bDone(ns)].map((v, i) => (
                            <td key={i} style={s.td}>
                              <span style={(!hideSections && v) ? s.doneTag : s.pendingTag}>{(!hideSections && v) ? '✓' : '-'}</span>
                            </td>
                          ))}
                          <td style={s.td}>
                            <button style={s.viewBtn} onClick={() => setSelectedEmployee(emp.id)}>View</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table></ScrollableTable>
              )}
            </div>
          )}

          {/* ── LEAVERS ── */}
          {activeSection === 'leavers' && (
            <>
              {/* LEAVERUI: the not-yet-started candidate LIST is removed. Starting a leaver process
                  now happens via this button + StartLeaverModal. CARDORDER: the three cards below
                  render actionable-first - In Progress, Draft, then Offboarded (the archive) last.
                  No leaver logic/endpoint changed. */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: isDarkMode ? '#e8e4d8' : '#1a1a2e' }}>Leavers</h2>
                <button
                  style={{ ...s.viewBtn, background: '#b91c1c', color: '#fff', border: 'none', padding: '9px 16px', fontWeight: 600 }}
                  onClick={() => setShowStartLeaver(true)}
                >
                  Start a leaver process
                </button>
              </div>

              {/* CARDORDER: actionable first - In Progress, then Draft, then Offboarded (the
                  archive) last, per today's design-audit decision. Pure rendering order; the
                  three derivations and every control inside the cards are untouched. */}
              <div style={s.card}>
                <h3 style={s.cardTitle}>
                  In Progress
                  {leaversInProgress.length > 0 && <span style={s.goldBadge}>{leaversInProgress.length}</span>}
                </h3>
                {/* PROCESSWORD: "process", not "forms" - the form is the document being filled,
                    the process is the overall leaver workflow (Craig's wording feedback). */}
                {leaversInProgress.length === 0 ? (
                  leaverDataFailed
                    ? <LoadError dark={isDarkMode} message="Couldn't load the leaver data - this list may be incomplete." onRetry={fetchData} />
                    : <p style={s.empty}>No leaver process in progress</p>
                ) : (
                  <ScrollableTable><table style={s.table}>
                    <thead>
                      <tr>
                        <th style={s.th}>Employee</th>
                        <th style={s.th}>Last working day</th>
                        <th style={s.th}>S1 AO</th>
                        <th style={s.th}>S2 IT</th>
                        <th style={s.th}>S3 HR</th>
                        <th style={s.th}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaversInProgress.map(emp => {
                        const lv = leaverForms[emp.id];
                        return (
                          <tr key={emp.id} style={s.tr}>
                            <td style={s.td}>
                              <p style={{ margin: '0 0 2px 0', fontWeight: 500, fontSize: '13px' }}>{emp.first_name} {emp.last_name}<InternBadge employee={emp} /></p>
                              <p style={{ margin: 0, fontSize: '11px', color: '#aaa' }}>{emp.company_email}</p>
                            </td>
                            <td style={s.td}>
                              <span style={{ fontSize: '12px', color: '#666' }}>
                                {lv?.last_working_date ? toDisplayDate(lv.last_working_date).toLocaleDateString() : '-'}
                              </span>
                            </td>
                            {['section1_completed', 'section2_completed', 'section3_completed'].map(k => (
                              <td key={k} style={s.td}>
                                <span style={lv?.[k] ? s.doneTag : s.pendingTag}>{lv?.[k] ? '✓' : '-'}</span>
                              </td>
                            ))}
                            <td style={s.td}>
                              <button style={s.viewBtn} onClick={() => setSelectedEmployee(emp.id)}>View</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table></ScrollableTable>
                )}
              </div>

              {/* LEAVERDRAFT: started-but-only-saved-as-draft leaver processes (Section 1 never
                  submitted). A separate category by confirmed COO decision - "In Progress" above
                  keeps meaning exactly section1_completed=true. Resume reopens Section 1 with the
                  draft loaded (same setLeaverEmployee path the start modal uses). */}
              <div style={{ ...s.card, marginTop: '20px' }}>
                <h3 style={s.cardTitle}>
                  Draft
                  {leaversDraft.length > 0 && <span style={s.goldBadge}>{leaversDraft.length}</span>}
                </h3>
                {leaversDraft.length === 0 ? (
                  leaverDataFailed
                    ? <LoadError dark={isDarkMode} message="Couldn't load the leaver data - this list may be incomplete." onRetry={fetchData} />
                    : <p style={s.empty}>No leaver drafts</p>
                ) : (
                  <ScrollableTable><table style={s.table}>
                    <thead>
                      <tr>
                        <th style={s.th}>Employee</th>
                        <th style={s.th}>Last working day</th>
                        <th style={s.th}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaversDraft.map(emp => {
                        const lv = leaverForms[emp.id];
                        return (
                          <tr key={emp.id} style={s.tr}>
                            <td style={s.td}>
                              <p style={{ margin: '0 0 2px 0', fontWeight: 500, fontSize: '13px' }}>{emp.first_name} {emp.last_name}<InternBadge employee={emp} /></p>
                              <p style={{ margin: 0, fontSize: '11px', color: '#aaa' }}>{emp.company_email}</p>
                            </td>
                            <td style={s.td}>
                              <span style={{ fontSize: '12px', color: '#666' }}>
                                {lv?.last_working_date ? toDisplayDate(lv.last_working_date).toLocaleDateString() : '-'}
                              </span>
                            </td>
                            <td style={s.td}>
                              <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                                <button style={s.viewBtn} onClick={() => { setLeaverFormSection(1); setLeaverEmployee(emp.id); }}>Resume</button>
                                {/* DRAFTCANCEL: coo/admin cancel of a draft started by mistake.
                                    Backend enforces the genuine-draft guard (409 once S1 is
                                    submitted); the row disappears from Draft and the employee
                                    becomes eligible again in the start modal. */}
                                <button
                                  style={{ ...s.viewBtn, borderColor: '#A63A3A', color: '#A63A3A' }}
                                  onClick={() => askConfirm({
                                    title: 'Cancel leaver draft',
                                    message: `Cancel the leaver draft for ${emp.first_name} ${emp.last_name}? The draft will be removed entirely.`,
                                    confirmLabel: 'Yes, cancel it',
                                    cancelLabel: 'Keep draft',
                                    danger: true,
                                    onConfirm: async () => {
                                      try {
                                        await axios.delete(`${API}/leaver/${emp.id}`);
                                        fetchData();
                                      } catch (err) {
                                        console.error(err);
                                      }
                                    },
                                  })}
                                >
                                  Cancel draft
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table></ScrollableTable>
                )}
              </div>

              {/* CARDORDER: Offboarded is the archive - deliberately LAST, below the two
                  actionable cards. Content and controls byte-identical, only the position moved. */}
              <div style={{ ...s.card, marginTop: '20px' }}>
                <h3 style={s.cardTitle}>
                  Offboarded employees
                  {leaversOffboarded.length > 0 && <span style={s.goldBadge}>{leaversOffboarded.length}</span>}
                </h3>
                {leaversOffboarded.length === 0 ? (
                  leaverDataFailed
                    ? <LoadError dark={isDarkMode} message="Couldn't load the leaver data - this list may be incomplete." onRetry={fetchData} />
                    : <p style={s.empty}>No offboarded employees yet</p>
                ) : (
                  <ScrollableTable><table style={s.table}>
                    <thead>
                      <tr>
                        <th style={s.th}>Employee</th>
                        <th style={s.th}>Last working day</th>
                        <th style={s.th}>Reason</th>
                        <th style={s.th}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaversOffboarded.map(emp => {
                        const lv = leaverForms[emp.id];
                        return (
                          <tr key={emp.id} style={s.tr}>
                            <td style={s.td}>
                              <p style={{ margin: '0 0 2px 0', fontWeight: 500, fontSize: '13px' }}>
                                {emp.first_name} {emp.last_name}<InternBadge employee={emp} />
                                {/* LEAVERARCHVIS: distinguish already-archived leavers from
                                    completed-but-not-yet-archived ones at a glance. */}
                                {emp.is_archived && (
                                  <span style={{ display: 'inline-block', marginLeft: '6px', fontSize: '10px', fontWeight: 600, color: '#888', border: '1px solid #999', borderRadius: '10px', padding: '1px 7px', verticalAlign: 'middle' }}>
                                    Archived
                                  </span>
                                )}
                              </p>
                              <p style={{ margin: 0, fontSize: '11px', color: '#aaa' }}>{emp.company_email}</p>
                            </td>
                            <td style={s.td}>
                              <span style={{ fontSize: '12px', color: '#666' }}>
                                {lv?.last_working_date ? toDisplayDate(lv.last_working_date).toLocaleDateString() : '-'}
                              </span>
                            </td>
                            <td style={s.td}>
                              <span style={{ fontSize: '12px', color: '#666' }}>
                                {lv?.reason_for_leaving ? String(lv.reason_for_leaving).replace(/_/g, ' ') : '-'}
                              </span>
                            </td>
                            <td style={s.td}>
                              <button style={s.viewBtn} onClick={() => setSelectedEmployee(emp.id)}>View</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table></ScrollableTable>
                )}
              </div>
            </>
          )}

          {/* ── INTERNVIEW: Active Interns (COO/admin) ── */}
          {activeSection === 'interns' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: isDarkMode ? '#e8e4d8' : '#1a1a2e' }}>Active Interns</h2>
              </div>
              <div style={s.card}>
                <h3 style={s.cardTitle}>
                  Current interns
                  {activeInterns.length > 0 && <span style={s.goldBadge}>{activeInterns.length}</span>}
                  {internsEnded.length > 0 && <span style={s.redBadge}>{internsEnded.length} ended</span>}
                </h3>
                {activeInterns.length === 0 ? <p style={s.empty}>No active interns</p> : (
                  <ScrollableTable><table style={s.table}>
                    <thead>
                      <tr>
                        <th style={s.th}>Name / Email</th>
                        <th style={s.th}>Entity</th>
                        <th style={s.th}>Term</th>
                        <th style={s.th}>End date</th>
                        <th style={s.th}>Status</th>
                        <th style={s.th}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeInterns.map(emp => (
                        <tr key={emp.id} style={{ ...s.tr, ...(emp._ended ? { backgroundColor: tokens(isDarkMode).critBg } : {}) }}>
                          <td style={s.td}>
                            <p style={{ margin: '0 0 2px 0', fontWeight: 500, fontSize: '13px' }}>{emp.first_name} {emp.last_name}</p>
                            <p style={{ margin: 0, fontSize: '11px', color: '#aaa' }}>{emp.company_email}</p>
                          </td>
                          <td style={s.td}><span style={{ fontSize: '12px', color: '#666' }}>{emp.primary_entity || '-'}</span></td>
                          <td style={s.td}>
                            <span style={{ fontSize: '12px', color: '#666' }}>
                              {emp.intern_term === 'short_term' ? 'Short-term'
                                : emp.intern_term === 'long_term'
                                  ? `Long-term${emp.intern_duration ? ` (${emp.intern_duration})` : ''}`
                                  : '-'}
                            </span>
                          </td>
                          <td style={s.td}>
                            <span style={{ fontSize: '12px', color: '#666' }}>
                              {/* _end is now a YYYY-MM-DD string; the +'T00:00:00' local-midnight
                                  parse is for DISPLAY formatting only and never feeds the ended
                                  decision (that comes from the shared helper on the raw string). */}
                              {emp._end ? toDisplayDate(emp._end + 'T00:00:00').toLocaleDateString() : '-'}
                            </span>
                          </td>
                          <td style={s.td}>
                            {!emp._end
                              ? <span style={s.noVisaPill}>No end date</span>
                              : emp._ended
                                ? <span style={s.overdueDays}><span style={s.pillDot} />Ended</span>
                                : <span style={s.noVisaPill}>{emp._daysRemaining} day{emp._daysRemaining === 1 ? '' : 's'} left</span>}
                          </td>
                          <td style={s.td}>
                            <button style={s.viewBtn} onClick={() => setSelectedEmployee(emp.id)}>View</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table></ScrollableTable>
                )}
              </div>
            </>
          )}

          {/* ── GRIEVANCES ── */}
          {activeSection === 'grievances' && <GrievanceList />}

          {/* HRSHELL: the LEAVECAL global view, mounted for the first time. The component, its
              API and its tests were complete and green but it was imported only by its own test,
              so no role could reach the calendars - that was LEAVECAL's open item 1.
              `global` renders all three absence types with the legend and the filters. THE SERVER
              DECIDES WHAT IS IN IT: scope_for() resolves hr to SCOPE_ALL with the real type, and a
              Country Lead to their own entity, so the same mount serves both correctly and this
              component needs no role prop. */}
          {/* LEAVECALHIDE: even reached by a hand-typed URL, the section renders
              nothing when the setting is off. The sidebar entry above is the
              convenience; this is the control. */}
          {leaveCalEnabled && activeSection === 'leavecal' && (
            <div style={s.card}>
              <h3 style={s.cardTitle}>{dashT('hrNav.leaveCalendarTitle')}</h3>
              <LeaveCalendar global />
            </div>
          )}

          {/* ABSREG point 3: the absence register. Same mount for both shells - the SERVER decides
              who may read it (hr/coo/admin only, point 3f), so this needs no role prop, exactly as
              the calendar above needs none. onOpenApprovals routes to the EXISTING Validation
              section rather than opening any approval control of its own (point 3e). */}
          {activeSection === 'absreg' && (
            <div style={s.card}>
              <h3 style={s.cardTitle}>{dashT('absenceRegister.title')}</h3>
              <AbsenceRegister
                dark={isDarkMode}
                onOpenApprovals={() => setActiveSection('validation')}
              />
            </div>
          )}

          {/* ── COMPLIANCE ── */}
          {activeSection === 'compliance' && (
            <ComplianceModule
              currentUser={currentUser}
              isAdmin={true}
              readOnly={true}
              userRole={role}
              onBack={() => setActiveSection('overview')}
            />
          )}

          {/* ── POLICIES ── */}
          {activeSection === 'policies' && (
            <PolicyRepository
              currentUser={currentUser}
              userRole={role}
              /* POLICYHR: canManage is now the SERVER'S OWN ALLOWLIST, not "everyone except one
                 role". The three policy writes (upload, PATCH, DELETE) are all
                 compliance/admin/coo server side (policies.py:82, :165, :181).

                 THE BUG THIS FIXES. CLREAD part 2 wrote `!isCountryLead`, which was correct for
                 the only two roles that reached this mount at the time (coo and country_lead).
                 HRSHELL then routed hr through this same component and recorded in the checklist
                 that policy upload was "already canManage, false for hr" - but isCountryLead is
                 `role === 'country_lead'`, so for hr it is FALSE and canManage came out TRUE. HR
                 was shown the Upload New Policy form and every attempt returned 403 from
                 policies.py:82, which is exactly the "control that fails on click" that table
                 existed to prevent. Reported from a real HR session, not from a test.

                 Naming the admitted roles positively means a role added to this portal later is
                 read-only here by default and has to be granted on purpose, rather than being
                 silently granted because it is not the one role someone thought to exclude. */
              canManage={POLICY_WRITE_ROLES.includes(role)}
              onBack={() => setActiveSection('overview')}
            />
          )}

          {/* ── RETENTION REVIEW ── */}
          {activeSection === 'retention' && (
            <RetentionReview
              currentUser={currentUser}
              userRole={role}
              onBack={() => setActiveSection('overview')}
            />
          )}

          {/* ── ANALYTICS ── */}
          {activeSection === 'analytics' && (() => {
            const statuses = ['Overdue', 'Pending', 'Done on time', 'Done late'];
            const fns = [];
            const tasks = [];
            const assigneeTask = null;
            return (
              <div style={{ margin: '-20px -24px', padding: '24px 24px 0', background: isDarkMode ? '#0f1a2e' : '#edecea' }}>
                <div style={{ marginBottom: '16px' }}>
                  <span
                    style={{ fontSize: '13px', color: '#9ba8c0', cursor: 'pointer', transition: 'color 0.12s' }}
                    onClick={() => setActiveSection('overview')}
                    onMouseEnter={e => e.currentTarget.style.color = '#c9a84c'}
                    onMouseLeave={e => e.currentTarget.style.color = '#9ba8c0'}
                  >Oversight</span>
                  <span style={{ fontSize: '13px', color: '#9ba8c0', margin: '0 6px' }}>›</span>
                  <span style={{ fontSize: '13px', color: '#1a2744', fontWeight: 500 }}>Analytics</span>
                </div>
                <h2 style={{ fontSize: '16px', fontWeight: 600, color: '#1a2744', margin: '0 0 20px 0' }}>Analytics</h2>
                <div style={{ display: 'grid', gridTemplateColumns: gridCols(isNarrow, 'repeat(4, 1fr)'), gap: '16px', alignItems: 'start' }}>

                  {/* Column 1: Status */}
                  <div style={s.card}>
                    <h3 style={{ ...s.cardTitle, color: '#1a2744' }}>Status</h3>
                    {statuses.map(status => {
                      const dotColour = STATUS_COLOURS[status];
                      return (
                        <button key={status}
                          onClick={() => { setAnalyticsStatus(status); setAnalyticsFn(null); setAnalyticsTask(null); setAnalyticsWorkflow(null); }}
                          style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            width: '100%', padding: '10px 12px', marginBottom: '6px', borderRadius: '6px',
                            border: analyticsStatus === status ? `2px solid ${dotColour}` : '1px solid #e8e4dc',
                            backgroundColor: analyticsStatus === status ? `${dotColour}18` : '#fff',
                            cursor: 'pointer', fontSize: '13px', textAlign: 'left',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ width: 9, height: 9, borderRadius: '50%', flexShrink: 0, backgroundColor: dotColour }} />
                            <span style={{ color: analyticsStatus === status ? dotColour : '#333', fontWeight: analyticsStatus === status ? 600 : 400 }}>{status}</span>
                          </div>
                          <span style={{ fontSize: '11px', color: '#aaa' }}>
                            {0}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Column 2: Function */}
                  <div style={s.card}>
                    <h3 style={{ ...s.cardTitle, color: '#1a2744' }}>Function</h3>
                    {!analyticsStatus ? (
                      <p style={s.empty}>Select a status</p>
                    ) : fns.length === 0 ? (
                      <p style={s.empty}>No functions</p>
                    ) : fns.map(fn => (
                      <button key={fn}
                        onClick={() => { setAnalyticsFn(fn); setAnalyticsTask(null); setAnalyticsWorkflow(null); }}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          width: '100%', padding: '10px 12px', marginBottom: '6px', borderRadius: '6px',
                          border: analyticsFn === fn ? '2px solid #1a2744' : '1px solid #e8e4dc',
                          backgroundColor: analyticsFn === fn ? '#1a274415' : '#fff',
                          cursor: 'pointer', fontSize: '13px', textAlign: 'left',
                        }}
                      >
                        <span style={{ color: analyticsFn === fn ? '#1a2744' : '#333', fontWeight: analyticsFn === fn ? 600 : 400 }}>{fn}</span>
                        <span style={{ fontSize: '11px', color: '#aaa' }}>
                          {0}
                        </span>
                      </button>
                    ))}
                  </div>

                  {/* Column 3: Task */}
                  <div style={s.card}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: analyticsWorkflow ? '8px' : 0 }}>
                      <h3 style={{ ...s.cardTitle, color: '#1a2744', marginBottom: 0 }}>Task</h3>
                      {analyticsWorkflow && (
                        <button
                          onClick={() => { setAnalyticsWorkflow(null); setAnalyticsTask(null); }}
                          style={{ fontSize: '11px', color: '#aaa', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                        >✕ clear</button>
                      )}
                    </div>
                    {analyticsWorkflow && (
                      <div style={{ fontSize: '12px', color: '#1a2744', backgroundColor: '#e8e4dc', borderRadius: '4px', padding: '3px 8px', marginBottom: '10px', fontWeight: 500, display: 'inline-block' }}>{analyticsWorkflow}</div>
                    )}
                    {!analyticsWorkflow && !analyticsFn ? (
                      <p style={s.empty}>Select a function</p>
                    ) : tasks.length === 0 ? (
                      <p style={s.empty}>No tasks</p>
                    ) : tasks.map(task => (
                      <button key={task.id}
                        onClick={() => setAnalyticsTask(task.id)}
                        style={{
                          display: 'block', width: '100%', padding: '10px 12px', marginBottom: '6px', borderRadius: '6px',
                          border: analyticsTask === task.id ? '2px solid #1a2744' : '1px solid #e8e4dc',
                          backgroundColor: analyticsTask === task.id ? '#1a274415' : '#fff',
                          cursor: 'pointer', fontSize: '13px', textAlign: 'left',
                          color: analyticsTask === task.id ? '#1a2744' : '#333',
                          fontWeight: analyticsTask === task.id ? 600 : 400,
                        }}
                      >
                        {task.title}
                      </button>
                    ))}
                  </div>

                  {/* Column 4: Assignee */}
                  <div style={s.card}>
                    <h3 style={{ ...s.cardTitle, color: '#1a2744' }}>Assignee</h3>
                    {!assigneeTask ? (
                      <p style={s.empty}>Select a task</p>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 0' }}>
                        <div style={{ width: 36, height: 36, borderRadius: '50%', backgroundColor: '#1a2744', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', fontWeight: 600, flexShrink: 0 }}>
                          {assigneeTask.assignee.charAt(0)}
                        </div>
                        <div>
                          <p style={{ margin: 0, fontWeight: 600, fontSize: '14px', color: '#1a2744' }}>{assigneeTask.assignee}</p>
                          <p style={{ margin: '3px 0 0 0', fontSize: '12px', color: '#aaa' }}>{assigneeTask.fn}</p>
                        </div>
                      </div>
                    )}
                  </div>

                </div>
              </div>
            );
          })()}

          {/* ── PERFORMANCE ── */}
          {activeSection === 'performance' && <PerformanceReport />}
          {/* TRADEFLOW: the two read-only desk views. Access is enforced server side; the
              sidebar simply does not offer a view this account may not open. */}
          {activeSection === 'trade-front-office' && <TradeActions desk="front_office" />}
          {activeSection === 'trade-back-office' && <TradeActions desk="back_office" />}

          {/* ── HR REPORT ── */}
          {activeSection === 'hr-report' && <HRReport onNavigate={navigate} />}

          {/* ── COMPLIANCE REPORT ── */}
          {/* CARDFILTER: onNavigate dropped - the summary cards filter the report's own detail
              list in place now, instead of routing to the Analytics skeleton. */}
          {activeSection === 'compliance-report' && <ComplianceReport />}

          {/* ── WORKFLOWS (COOWORKFLOWSPLIT: 3 scoped entry points, 1 shared component) ── */}
          {activeSection === 'workflows' && (
            <WorkflowList onBack={() => setActiveSection('overview')} currentUserRole={role}
              scope="involved" title="Workflows" />
          )}
          {activeSection === 'financial-workflows' && (
            <WorkflowList onBack={() => setActiveSection('overview')} currentUserRole={role}
              scope="finance" title="Financial workflows" />
          )}
          {activeSection === 'compliance-workflows' && (
            <WorkflowList onBack={() => setActiveSection('overview')} currentUserRole={role}
              scope="compliance" title="Compliance workflows" />
          )}

          {/* ── APP REQUESTS ── */}
          {activeSection === 'requests' && (
            <ApplicationRequestDashboard
              currentUser={currentUser}
              onBack={() => setActiveSection('overview')}
              // SHOWREFUSEDBTN: Dashboard.js is always mounted as the coo shell, so this is a
              // fixed fact about which shell renders here, not a derived/looked-up value.
              //
              // HRAPPREQ: no longer a fixed fact, because this component now ALSO serves the HR
              // shell. isCoo renders the approve/reject and live/KO decision controls, and those
              // endpoints are gated _require_technology_or_coo (application_requests.py:504, 656,
              // 680), which refuses hr. Passing it for an hr account would show HR four buttons
              // that 403 on click, which is exactly what the HR build set out to avoid.
              isCoo={!isHr}
            />
          )}

          {/* ── VALIDATION ── */}
          {activeSection === 'validation' && <ValidationView />}

            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Styles (dark-mode-aware) ───────────────────────────────────────────────────
// OPSTASKSALL: mkS lives in dashboardStyles.js, shared with the other shells' Tasks pages.
const mkM = (dark) => {
  const M = tokens(dark);
  return {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { backgroundColor: dark ? '#1a2744' : '#fff', borderRadius: '12px', padding: '28px', width: '600px', maxWidth: '90vw', boxShadow: '0 8px 32px rgba(0,0,0,0.2)', maxHeight: '80vh', overflowY: 'auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  title: { fontSize: '18px', fontWeight: '600', color: dark ? '#e8e4d8' : '#1a1a2e', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' },
  countBadge: { backgroundColor: M.warnBg, color: M.warnFg, borderRadius: RADIUS.pill, padding: '2px 8px', fontSize: '11.5px', fontWeight: 590 },
  closeBtn: { background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: dark ? '#9ba8c0' : '#666' },
  employeeList: { display: 'flex', flexDirection: 'column', gap: '12px' },
  employeeCard: { border: `1px solid ${dark ? 'rgba(201,168,76,0.15)' : '#eee'}`, borderRadius: '8px', overflow: 'hidden' },
  employeeRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', cursor: 'pointer', backgroundColor: dark ? '#1a2744' : '#fff' },
  visaCard: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', border: `1px solid ${dark ? 'rgba(201,168,76,0.15)' : '#eee'}`, borderRadius: '8px', cursor: 'pointer', backgroundColor: dark ? '#1a2744' : '#fff' },
  employeeLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  avatar: { width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#1a1a2e', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', fontWeight: '600', flexShrink: 0 },
  employeeName: { fontSize: '15px', fontWeight: '600', color: dark ? '#e8e4d8' : '#1a1a2e', margin: '0 0 2px 0' },
  taskCount: { fontSize: '12px', color: M.warnFg, margin: '0', fontWeight: 560 },
  expandHint: { fontSize: '11px', color: dark ? '#9ba8c0' : '#aaa', fontStyle: 'italic' },
  viewProfileBtn: { backgroundColor: '#1a1a2e', color: '#fff', border: 'none', borderRadius: '6px', padding: '6px 12px', fontSize: '13px', cursor: 'pointer' },
  taskList: { borderTop: `1px solid ${dark ? 'rgba(201,168,76,0.15)' : '#eee'}`, backgroundColor: dark ? '#0f1a2e' : '#f8f9fa', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '8px' },
  taskItem: { display: 'flex', alignItems: 'center', gap: '10px' },
  taskDot: { width: '6px', height: '6px', borderRadius: '50%', backgroundColor: M.warnFg, flexShrink: 0 },
  taskTitle: { fontSize: '13px', color: dark ? '#e8e4d8' : '#333', margin: '0 0 2px 0', fontWeight: '500' },
  taskDue: { fontSize: '12px', color: dark ? '#9ba8c0' : '#888', margin: '0' },
  visaEmail: { fontSize: '12px', color: dark ? '#9ba8c0' : '#888', margin: '0' },
  visaBadge: { backgroundColor: M.critBg, color: M.critFg, borderRadius: RADIUS.pill, padding: '2px 8px', fontSize: '11.5px', fontWeight: 590 },
  empty: { color: M.tx3, fontSize: '12.5px' },
  };
};

export default Dashboard;
